import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import * as d3 from "d3";
import bedroomImg from "../../../common-components/structures/bedroom.png";
import kitchenImg from "../../../common-components/structures/kitchen.png";
import toiletImg from "../../../common-components/structures/bathroom.png";
import livingRoomImg from "../../../common-components/structures/livingroom.png";
import officeImg from "../../../common-components/structures/office.png";
import diningImg from "../../../common-components/structures/diningroom.png";
import { AppDispatch, RootState } from "../../../redux/store";
import { useSelector, useDispatch } from "react-redux";
import { Room, Wall, WallWithoutId, Label } from "../components/Floorplan";
import {
  drawGrid,
  getWallLimits,
  getRandomColor,
  getValidColor,
  getRoomLimits,
  getFloorplanLimits,
} from "../utils/helpers";
import { LINEWIDTH } from "../utils/constants";
import {
  clearRedoStacks,
  pushUndoStacks,
  setAssetType,
  setCleanUpDone,
  setCleanUpRequired,
  setPlot,
  setRooms,
  setSelectedWall,
  setSelectedWallOption,
  setWalls,
  setLabels,
  setLockRooms,
  setWallTransformationUsed,
  setDoorAssets,
  setWindowAssets,
} from "../../../redux/features/appState";
import { v4 as uuidv4 } from "uuid";
import { useRoomCleanUp } from "./useRoomCleanup";
import { formatLabel } from "../../../utils/helpers";
import { showToast } from "../../../redux/features/toast/toast.slice";

export const getViewportDimension = (isWidth: boolean): number => {
  if (typeof window === "undefined" || !window.visualViewport) {
    return isWidth ? 1920 : 1080; // Fallback values
  }
  return isWidth ? window.visualViewport.width : window.visualViewport.height;
};

// Updated scaling function
export const scaleCoordinate = (
  coord: number,
  isX: boolean,
  width: number,
  height: number,
): number => {
  const safeHeight = height && height > 0 ? height : 1080;
  const viewportDimension = getViewportDimension(isX);
  const offset = viewportDimension * 0.3;
  const scaleFactor = (getViewportDimension(false) * 0.5) / safeHeight;
  return coord * scaleFactor + offset;
};

// Updated unscaling function
export const unscaleCoordinate = (
  scaledCoord: number,
  isX: boolean,
  width: number,
  height: number,
): number => {
  const safeHeight = height && height > 0 ? height : 1080;
  const viewportDimension = getViewportDimension(isX);
  const offset = viewportDimension * 0.3;
  const scaleFactor = (getViewportDimension(false) * 0.5) / safeHeight;
  return (scaledCoord - offset) / scaleFactor;
};

/**
 * Merges collinear, adjacent walls that share the same roomIds into single walls.
 */
const mergeCollinearWalls = (wallsToMerge: Wall[]): Wall[] => {
  const TOLERANCE = 1;

  const isHorizontal = (w: Wall): boolean => Math.abs(w.y1 - w.y2) < TOLERANCE;
  const isVertical = (w: Wall): boolean => Math.abs(w.x1 - w.x2) < TOLERANCE;
  const roomIdsKey = (roomIds: string[] | undefined): string =>
    (roomIds || []).slice().sort().join(",");

  const merged: Wall[] = [];

  const horizontalWalls = wallsToMerge.filter((w: Wall) => isHorizontal(w));
  const verticalWalls = wallsToMerge.filter((w: Wall) => isVertical(w));
  const diagonalWalls = wallsToMerge.filter(
    (w: Wall) => !isHorizontal(w) && !isVertical(w),
  );

  const hGroups: { [key: string]: Wall[] } = {};
  horizontalWalls.forEach((w: Wall) => {
    const y = Math.round(w.y1);
    const key = `h_${y}_${roomIdsKey(w.roomIds)}`;
    if (!hGroups[key]) hGroups[key] = [];
    hGroups[key].push(w);
  });

  Object.keys(hGroups).forEach((key: string) => {
    const group = hGroups[key];
    const sorted: Wall[] = group
      .map(
        (w: Wall): Wall => ({
          ...w,
          x1: Math.min(w.x1, w.x2),
          x2: Math.max(w.x1, w.x2),
          y1: w.y1,
          y2: w.y1,
        }),
      )
      .sort((a: Wall, b: Wall) => a.x1 - b.x1);

    let current: Wall = { ...sorted[0] };
    for (let i = 1; i < sorted.length; i++) {
      const next = sorted[i];
      if (next.x1 <= current.x2 + TOLERANCE) {
        current = { ...current, x2: Math.max(current.x2, next.x2) };
      } else {
        merged.push(current);
        current = { ...next };
      }
    }
    merged.push(current);
  });

  const vGroups: { [key: string]: Wall[] } = {};
  verticalWalls.forEach((w: Wall) => {
    const x = Math.round(w.x1);
    const key = `v_${x}_${roomIdsKey(w.roomIds)}`;
    if (!vGroups[key]) vGroups[key] = [];
    vGroups[key].push(w);
  });

  Object.keys(vGroups).forEach((key: string) => {
    const group = vGroups[key];
    const sorted: Wall[] = group
      .map(
        (w: Wall): Wall => ({
          ...w,
          x1: w.x1,
          x2: w.x1,
          y1: Math.min(w.y1, w.y2),
          y2: Math.max(w.y1, w.y2),
        }),
      )
      .sort((a: Wall, b: Wall) => a.y1 - b.y1);

    let current: Wall = { ...sorted[0] };
    for (let i = 1; i < sorted.length; i++) {
      const next = sorted[i];
      if (next.y1 <= current.y2 + TOLERANCE) {
        current = { ...current, y2: Math.max(current.y2, next.y2) };
      } else {
        merged.push(current);
        current = { ...next };
      }
    }
    merged.push(current);
  });
  merged.push(...diagonalWalls);

  return merged;
};

const recalculateLabels = (updatedRooms: Room[], currentLabels: Label[]): Label[] => {
  const updatedLabels: Label[] = [];
  (currentLabels || []).forEach((label) => {
    const room = updatedRooms?.find((r) => String(r.id) === String(label.roomId));
    if (room) {
      if (room.walls && room.walls.length > 0) {
        let minX = Number.MAX_VALUE;
        let maxX = Number.MIN_VALUE;
        let minY = Number.MAX_VALUE;
        let maxY = Number.MIN_VALUE;

        room.walls.forEach((w) => {
          minX = Math.min(minX, w.x1, w.x2);
          maxX = Math.max(maxX, w.x1, w.x2);
          minY = Math.min(minY, w.y1, w.y2);
          maxY = Math.max(maxY, w.y1, w.y2);
        });

        if (minX !== Number.MAX_VALUE && maxX !== Number.MIN_VALUE) {
          updatedLabels.push({
            ...label,
            x: (minX + maxX) / 2,
            y: (minY + maxY) / 2,
          });
          return;
        }
      }
      updatedLabels.push(label);
    }
  });
  return updatedLabels;
};

export const useFloorPlan = (
  svgRef: React.MutableRefObject<SVGSVGElement | null>,
  contentGroupRef: React.MutableRefObject<SVGGElement | null>,
  zoomTransform: React.MutableRefObject<d3.ZoomTransform>,
  fromRoomPlanner: boolean,
) => {
  const [selectedLineId, setSelectedLineId] = useState<string>();
  const index = useSelector((state: RootState) => state.graph.index);
  const dimensioned = useSelector(
    (state: RootState) => state.graph.dimensioned,
  );
  const cleanUpDone = useSelector(
    (state: RootState) => state.outputEditorButtonState.cleanUpDone,
  );
  const newAsset = useSelector(
    (state: RootState) => state.outputEditorButtonState.addAsset,
  );
  const roomNameMapping = useSelector(
    (state: RootState) => state.graph.roomNameMapping,
  );
  let contentGroup: d3.Selection<SVGGElement, unknown, null, undefined>;
  const rooms = useSelector(
    (state: RootState) => state.outputEditorButtonState.rooms || [],
  );
  const floorPlans = useSelector((state: RootState) => state.graph.floorPlans);
  const walls = useSelector(
    (state: RootState) => state.outputEditorButtonState.walls || [],
  );
  const labels = useSelector(
    (state: RootState) => state.outputEditorButtonState.labels || [],
  );
  const doorAssets = useSelector(
    // Add selector for door assets
    (state: RootState) => state.outputEditorButtonState.doorAssets || [],
  );
  const windowAssets = useSelector(
    // Add selector for window assets
    (state: RootState) => state.outputEditorButtonState.windowAssets || [],
  );
  const showPlot = useSelector(
    (state: RootState) => state.outputEditorButtonState.showPlot,
  );
  const floorplan_height = useSelector(
    (state: RootState) => state.outputEditorButtonState.height,
  );
  const floorplan_width = useSelector(
    (state: RootState) => state.outputEditorButtonState.width,
  );
  const plot_boundary = useSelector(
    (state: RootState) => state.outputEditorButtonState.plot_boundary,
  );
  const draw = useSelector(
    (state: RootState) => state.outputEditorButtonState.drawLine,
  );
  const unit = useSelector(
    (state: RootState) => state.outputEditorButtonState.unit,
  );
  const selectedWall = useSelector(
    (state: RootState) => state.outputEditorButtonState.selectedWall,
  );
  const selectedWallOption = useSelector(
    (state: RootState) => state.outputEditorButtonState.selectedWallOption,
  );
  const activeTool = useSelector(
    (state: RootState) => state.outputEditorButtonState.activeTool,
  );
  const lockRooms = useSelector(
    (state: RootState) => state.outputEditorButtonState.lockRooms,
  );
  const selectedRef = useRef(
    activeTool === "select" || activeTool === "arrow" || activeTool === null,
  );
  const selectedWallRef = useRef(selectedWall); // Ref to track selected
  const lockRoomsRef = useRef(lockRooms);
  const doorAssetsRef = useRef(doorAssets);
  const windowAssetsRef = useRef(windowAssets);
  // Refs to avoid stale closures in SVG click/drag handlers
  const activeToolRef = useRef(activeTool);
  const drawRef = useRef(draw);
  const wallsRef = useRef(walls);
  const selectedWallOptionRef = useRef(selectedWallOption);
  const roomsRef = useRef(rooms);
  const plotBoundaryRef = useRef(plot_boundary);

  const [initialUnits, setInitialUnits] = useState("");

  useEffect(() => {
    activeToolRef.current = activeTool;
    // Always keep selectedRef in sync with activeTool
    selectedRef.current =
      activeTool === "select" || activeTool === "arrow" || activeTool === null;
    if (selectedWallOption !== null) {
      selectedWallRef.current = selectedWall;
    }
  }, [selectedWallOption, activeTool, selectedWall]);
  useEffect(() => {
    drawRef.current = draw;
  }, [draw]);
  useEffect(() => {
    wallsRef.current = walls;
  }, [walls]);
  useEffect(() => {
    roomsRef.current = rooms;
  }, [rooms]);
  useEffect(() => {
    plotBoundaryRef.current = plot_boundary;
  }, [plot_boundary]);
  useEffect(() => {
    selectedWallOptionRef.current = selectedWallOption;
  }, [selectedWallOption]);
  useEffect(() => {
    lockRoomsRef.current = lockRooms;
  }, [lockRooms]);
  useEffect(() => {
    doorAssetsRef.current = doorAssets;
  }, [doorAssets]);
  useEffect(() => {
    windowAssetsRef.current = windowAssets;
  }, [windowAssets]);

  useLayoutEffect(() => {
    setInitialUnits(unit);
  }, []);

  const dispatch = useDispatch<AppDispatch>();
  const drawnWallsRef: React.MutableRefObject<WallWithoutId[]> = useRef([]);
  const tempRoomsRef: React.MutableRefObject<Room[]> = useRef([]);

  // Function to calculate wall length using unscaled coordinates
  const calculateUnscaledWallLength = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    width: number,
    height: number,
  ): number => {
    const unscaledX1 = unscaleCoordinate(x1, true, width, height);
    const unscaledY1 = unscaleCoordinate(y1, false, width, height);
    const unscaledX2 = unscaleCoordinate(x2, true, width, height);
    const unscaledY2 = unscaleCoordinate(y2, false, width, height);

    return Math.sqrt(
      Math.pow(unscaledX2 - unscaledX1, 2) +
        Math.pow(unscaledY2 - unscaledY1, 2),
    );
  };

  const convertUnitsToMeters = (units: number): string => {
    if (initialUnits === "meters") {
      return `${units.toFixed(2)} m`;
    }
    const meters = units * 0.3048; // Convert feet to meters
    return `${meters.toFixed(2)} m`; // Round to 2 decimal places for readability
  };

  // Conversion function (assuming input is in the original units, likely feet)
  const convertUnitsToFeetAndInches = (units: number): string => {
    if (initialUnits === "meters") {
      const feetTotal = units / 0.3048;
      const feet = Math.floor(feetTotal);
      const inches = Math.round((feetTotal - feet) * 12);
      if (inches === 12) {
        return `${feet + 1} ' 0"`;
      }

      return `${feet}' ${inches}"`;
    }
    const feet = Math.floor(units);
    const inches = Math.round((units - feet) * 12);

    if (inches === 12) {
      return `${feet + 1}' 0"`;
    }

    return `${feet}' ${inches}"`;
  };

  const fromDocuments = useSelector(
    (store: RootState) => store.graph?.fromDocuments,
  );
  const selectedTemplate = useSelector(
    // 👈 add this
    (state: RootState) => state.templateState.selectedTemplate,
  );

  useEffect(() => {
    // console.log("doRoomCleanUp 2", cleanUpDone);
    if (!cleanUpDone) return;
    if (!svgRef.current) return;
    if (!svgRef.current.viewBox.baseVal) return;
    drawnWallsRef.current = [];

    d3.select(svgRef.current).selectAll("*").remove();

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    walls?.forEach((wall) => {
      minX = Math.min(minX, wall.x1, wall.x2);
      minY = Math.min(minY, wall.y1, wall.y2);
      maxX = Math.max(maxX, wall.x1, wall.x2);
      maxY = Math.max(maxY, wall.y1, wall.y2);
    });

    // Handle case where no walls are present (e.g., empty floorplan)
    if (minX === Infinity) {
      minX = 0;
      maxX = 100; // Default sensible values if no walls
      minY = 0;
      maxY = 100;
    }

    const floorplanWidth = maxX - minX;
    const floorplanHeight = maxY - minY;

    const svg = d3
      .select(svgRef.current)
      .attr("width", "100%")
      .attr("height", "100%");

    // Recalculate scale factor after viewBox is set
    const scaleX = svgRef.current.clientWidth / floorplanWidth;
    const scaleY = svgRef.current.clientHeight / floorplanHeight;
    const scale = Math.min(scaleX, scaleY);

    // Create a group that will contain everything
    const mainGroup = svg.append("g").attr("class", "main-group");

    // Grid group
    const gridGroup = mainGroup.append("g").attr("class", "grid-group");
    drawGrid(gridGroup, svgRef);

    // Content group
    const floorplanGroup = mainGroup
      .append("g")
      .attr("class", "floorplan-group");

    contentGroupRef.current = floorplanGroup
      .append("g")
      .attr("class", "content-group")
      .node();

    // contentGroupRef?.current?.attr("transform", `scale(${scale})`);
    // Define and apply zoom behavior
    const zoomBehavior = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.5, 2])
      .translateExtent([
        [0, 0],
        [window.innerWidth, window.innerHeight],
      ])
      .on("zoom", (event) => {
        zoomTransform.current = event.transform;
        mainGroup.attr("transform", event.transform);
      });

    svg.call(zoomBehavior).on("dblclick.zoom", null).on("mousedown.zoom", null);

    if (!Array.isArray(rooms)) {
      console.error("Error: `rooms` is not an array or is undefined", rooms);
      return;
    }

    const svgElement = svgRef.current;
    if (!svgElement) {
      console.error("Error: `svgRef.current` is null or undefined.");
      return;
    }

    // Room Dragging Feature
    const roomDrag = d3
      .drag<SVGPathElement, { roomId: string; roomName: string }>()
      .on("start", function (event, d) {
        // movement restricted to the room
        if (lockRoomsRef.current) {
          event.sourceEvent.stopPropagation();
          return;
        }
        event.sourceEvent.stopPropagation();
        // track movement of room
        if (!(this as any).currentPosition) {
          (this as any).currentPosition = { x: 0, y: 0 };
        }

        const bbox = (this as SVGGraphicsElement).getBBox();
        const bufferWidth = LINEWIDTH * 2;
        // border to the dragged room
        d3.select((this as Element).parentNode as Element)
          .append("rect")
          .attr("class", "drag-buffer-zone")
          .attr("x", bbox.x - bufferWidth)
          .attr("y", bbox.y - bufferWidth)
          .attr("width", bbox.width + bufferWidth * 2)
          .attr("height", bbox.height + bufferWidth * 2)
          .attr("fill", "none")
          .attr("stroke", "#666")
          .attr("stroke-width", bufferWidth)
          .attr("stroke-dasharray", "4,4")
          .attr("pointer-events", "none");

        d3.select(this).style("cursor", "grabbing").attr("opacity", 0.8);
      })
      // Prevent dragging if locked
      .on("drag", function (event) {
        if (lockRoomsRef.current) {
          return;
        }
        const dragBox = this as any;
        const offset = dragBox.currentPosition;
        // coordinates of room after moved
        const getBox = (el: SVGGraphicsElement) => {
          const bbox = el.getBBox();
          const moved = (el as any).currentPosition || { x: 0, y: 0 };
          return {
            x: bbox.x + moved.x,
            y: bbox.y + moved.y,
            w: bbox.width,
            h: bbox.height,
            right: bbox.x + moved.x + bbox.width,
            bottom: bbox.y + moved.y + bbox.height,
          };
        };
        let dx = event.dx;
        let dy = event.dy;
        const boxX = getBox(this as any);
        // Prevent Horizontal Overlapping
        d3.selectAll<SVGPathElement, any>(".room-area")
          .filter((n) => n !== this)
          .each((_, i, nodes) => {
            const b = getBox(nodes[i] as SVGGraphicsElement);
            const verticalOverlap = boxX.y < b.bottom && boxX.bottom > b.y;
            if (!verticalOverlap) return;
            if (dx > 0 && boxX.right <= b.x) {
              dx = Math.min(dx, b.x - boxX.right);
            }
            if (dx < 0 && boxX.x >= b.right) {
              dx = Math.max(dx, b.right - boxX.x);
            }
          });
        offset.x += dx;
        const boxY = getBox(this as any);
        // Prevent Vertical Overlapping
        d3.selectAll<SVGPathElement, any>(".room-area")
          .filter((n) => n !== this)
          .each((_, i, nodes) => {
            const b = getBox(nodes[i] as SVGGraphicsElement);
            const horizontalOverlap = boxY.x < b.right && boxY.right > b.x;
            if (!horizontalOverlap) return;
            if (dy > 0 && boxY.bottom <= b.y) {
              dy = Math.min(dy, b.y - boxY.bottom);
            }
            if (dy < 0 && boxY.y >= b.bottom) {
              dy = Math.max(dy, b.bottom - boxY.y);
            }
          });
        offset.y += dy;
        const SNAP_THRESHOLD = 50; // pixels
        const draggedBox = getBox(this as any);

        const draggedCorners = [
          { x: draggedBox.x, y: draggedBox.y }, // top-left
          { x: draggedBox.right, y: draggedBox.y }, // top-right
          { x: draggedBox.x, y: draggedBox.bottom }, // bottom-left
          { x: draggedBox.right, y: draggedBox.bottom }, // bottom-right
        ];

        let snapDx = 0;
        let snapDy = 0;
        let closestSnapDist = SNAP_THRESHOLD;

        d3.selectAll<SVGPathElement, any>(".room-area")
          .filter((n) => n !== this)
          .each((_, i, nodes) => {
            const otherBox = getBox(nodes[i] as SVGGraphicsElement);

            //corners of other rooms
            const otherCorners = [
              { x: otherBox.x, y: otherBox.y }, // top-left
              { x: otherBox.right, y: otherBox.y }, // top-right
              { x: otherBox.x, y: otherBox.bottom }, // bottom-left
              { x: otherBox.right, y: otherBox.bottom }, // bottom-right
            ];

            for (const dc of draggedCorners) {
              for (const oc of otherCorners) {
                const dist = Math.sqrt(
                  Math.pow(dc.x - oc.x, 2) + Math.pow(dc.y - oc.y, 2),
                );
                if (dist < closestSnapDist) {
                  closestSnapDist = dist;
                  snapDx = oc.x - dc.x;
                  snapDy = oc.y - dc.y;
                }
              }
            }
          });

        offset.x += snapDx;
        offset.y += snapDy;

        d3.select(this).attr(
          "transform",
          `translate(${offset.x}, ${offset.y})`,
        );
        d3.select(".drag-buffer-zone").attr(
          "transform",
          `translate(${offset.x}, ${offset.y})`,
        );
      })
      .on("end", function (event, d) {
        if (lockRoomsRef.current) {
          return;
        }

        const dragBox = this as any;
        const offset = dragBox.currentPosition || { x: 0, y: 0 };
        const dx = offset.x;
        const dy = offset.y;
        d3.selectAll(".drag-buffer-zone").remove();
        d3.select(this).style("cursor", "grab").attr("opacity", 1);
        if (dx === 0 && dy === 0) {
          return;
        }
        const draggedRoom = rooms?.find((room) => room.id === d.roomId);
        if (!draggedRoom) {
          console.warn("No dragged room found for id:", d.roomId);
          return;
        }
        if (plot_boundary && plot_boundary.length > 0 && showPlot) {
          let plotMinX = Infinity,
            plotMaxX = -Infinity;
          let plotMinY = Infinity,
            plotMaxY = -Infinity;
          plot_boundary.forEach((pw) => {
            plotMinX = Math.min(plotMinX, pw.x1, pw.x2);
            plotMaxX = Math.max(plotMaxX, pw.x1, pw.x2);
            plotMinY = Math.min(plotMinY, pw.y1, pw.y2);
            plotMaxY = Math.max(plotMaxY, pw.y1, pw.y2);
          });

          const bbox = (this as SVGGraphicsElement).getBBox();
          const finalX = bbox.x + dx;
          const finalY = bbox.y + dy;
          const finalRight = finalX + bbox.width;
          const finalBottom = finalY + bbox.height;

          if (
            finalX < plotMinX ||
            finalRight > plotMaxX ||
            finalY < plotMinY ||
            finalBottom > plotMaxY
          ) {
            d3.select(this).attr("transform", "translate(0, 0)");
            dragBox.currentPosition = { x: 0, y: 0 };
            dispatch(
              showToast({
                message: "Room cannot be moved outside the plot boundary",
                type: "error",
              }),
            );
            return;
          }
        }
        dispatch(clearRedoStacks());
        dispatch(pushUndoStacks({ rooms, walls, plot: plot_boundary }));
        const newWalls: Wall[] = [];
        const updatedWalls =
          walls?.map((wall) => {
            if (!wall.roomIds?.includes(draggedRoom.id)) {
              return wall;
            }
            const isSharedWall = wall.roomIds.length > 1;
            if (isSharedWall) {
              const newWall: Wall = {
                id: uuidv4(),
                x1: wall.x1 + dx,
                y1: wall.y1 + dy,
                x2: wall.x2 + dx,
                y2: wall.y2 + dy,
                roomIds: [draggedRoom.id],
              };
              newWalls.push(newWall);
              return {
                ...wall,
                roomIds: wall.roomIds.filter((rid) => rid !== draggedRoom.id),
              };
            } else {
              return {
                ...wall,
                x1: wall.x1 + dx,
                y1: wall.y1 + dy,
                x2: wall.x2 + dx,
                y2: wall.y2 + dy,
              };
            }
          }) || [];
        const finalWalls = mergeCollinearWalls([...updatedWalls, ...newWalls]);
        const updatedRooms =
          rooms?.map((room) => {
            const roomWalls = finalWalls.filter(
              (w) => w.roomIds?.includes(room.id),
            );
            return { ...room, walls: roomWalls };
          }) || [];
        const updatedLabels = recalculateLabels(updatedRooms, labels);
        dispatch(setWalls(finalWalls));
        dispatch(setLabels(updatedLabels));
        dispatch(setRooms(updatedRooms));
        dispatch(setCleanUpDone(false));
        setTimeout(() => dispatch(setCleanUpDone(true)), 10);
        dragBox.currentPosition = { x: 0, y: 0 };
      });

    svg.selectAll(".room-area").remove();

    let roomPolygons: [number, number][][] = [];

    rooms.forEach((room, index) => {
      if (!room || !Array.isArray(room.walls)) {
        console.warn("Skipping room due to missing walls:", room);
        return;
      }

      if (room.walls.length === 0) return;

      // Build polygon by tracing walls in connected order
      const TOLERANCE = 2;
      const pointsMatch = (
        ax: number,
        ay: number,
        bx: number,
        by: number,
      ): boolean =>
        Math.abs(ax - bx) < TOLERANCE && Math.abs(ay - by) < TOLERANCE;

      // Each wall is a segment: [{x1,y1} -> {x2,y2}]
      const segments = room.walls
        .filter(
          (w) =>
            typeof w?.x1 === "number" &&
            typeof w?.y1 === "number" &&
            typeof w?.x2 === "number" &&
            typeof w?.y2 === "number",
        )
        .map((w) => ({ x1: w.x1, y1: w.y1, x2: w.x2, y2: w.y2 }));

      if (segments.length < 2) return;

      // Trace the walls to build an ordered polygon
      const used = new Array(segments.length).fill(false);
      const orderedPoints: [number, number][] = [];

      // Start with first segment
      used[0] = true;
      orderedPoints.push([segments[0].x1, segments[0].y1]);
      orderedPoints.push([segments[0].x2, segments[0].y2]);

      let currentX = segments[0].x2;
      let currentY = segments[0].y2;

      for (let iter = 1; iter < segments.length; iter++) {
        let foundNext = false;
        for (let j = 0; j < segments.length; j++) {
          if (used[j]) continue;
          const seg = segments[j];
          if (pointsMatch(currentX, currentY, seg.x1, seg.y1)) {
            used[j] = true;
            orderedPoints.push([seg.x2, seg.y2]);
            currentX = seg.x2;
            currentY = seg.y2;
            foundNext = true;
            break;
          } else if (pointsMatch(currentX, currentY, seg.x2, seg.y2)) {
            used[j] = true;
            orderedPoints.push([seg.x1, seg.y1]);
            currentX = seg.x1;
            currentY = seg.y1;
            foundNext = true;
            break;
          }
        }
        if (!foundNext) break; // Can't trace further
      }

      if (orderedPoints.length > 2) {
        // Shrink the polygon slightly to keep walls visible
        const centerX = d3.mean(orderedPoints.map((p) => p[0]))!;
        const centerY = d3.mean(orderedPoints.map((p) => p[1]))!;
        const shrinkFactor = 0.98;

        const shrunkPolygon: [number, number][] = orderedPoints.map(
          ([x, y]) => [
            centerX + (x - centerX) * shrinkFactor,
            centerY + (y - centerY) * shrinkFactor,
          ],
        );

        roomPolygons.push(shrunkPolygon);

        const roomPath = svg
          .select(".floorplan-group")
          .append("path")
          .attr("class", "room-area")
          .datum({ roomId: room.id, roomName: room.name })
          .attr("d", `M${shrunkPolygon.map((p) => p.join(",")).join("L")}Z`)
          .attr("fill", getValidColor(room.color, index))
          .attr("stroke", "none")
          .style("cursor", "grab")
          .call(roomDrag);

        roomPath.lower();
      }
    });

    {
      /*
    // Check if there are valid room polygons before drawing
    if (roomPolygons.length === 0) {
      console.warn("No valid room polygons found.");
      return;
    }


    
    // Convert room polygons to SVG paths
    const mergedPath = roomPolygons
      .map((polygon) => `M${polygon.map((p) => p.join(",")).join("L")}Z`)
      .join(" ");

    let roomArea = svg
      .select(".floorplan-group")
      .append("path")
      .attr("class", "room-area")
      .attr("d", mergedPath)
      .attr("fill", "#fff7ec") // Adjust transparency if needed
      .attr("stroke", "none"); // Remove stroke to avoid overlaying walls
    roomArea.lower();
    */
    }

    if (fromRoomPlanner) {
      // Debug: Log all rooms to see their structure
      console.log(
        "All rooms data:",
        rooms.map((r) => ({
          id: r.id,
          name: r.name,
          roomtype: r.roomtype,
          // Log the entire room object for the first few rooms
          fullObject: rooms.indexOf(r) < 3 ? r : undefined,
        })),
      );

      rooms.forEach((room) => {
        if (!room || !room.walls || room.walls.length === 0) return;

        let minX = Infinity,
          minY = Infinity,
          maxX = -Infinity,
          maxY = -Infinity;

        room.walls.forEach((wall) => {
          minX = Math.min(minX, wall.x1, wall.x2);
          minY = Math.min(minY, wall.y1, wall.y2);
          maxX = Math.max(maxX, wall.x1, wall.x2);
          maxY = Math.max(maxY, wall.y1, wall.y2);
        });

        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        // Try multiple possible sources for room type
        let roomTypeValue = "";

        // Check all possible places where room type might be stored
        // This handles both dimensionless and dimensioned API responses
        if (room.roomtype) {
          roomTypeValue = room.roomtype;
        } else if ((room as any).type) {
          roomTypeValue = (room as any).type;
        } else if ((room as any).roomType) {
          roomTypeValue = (room as any).roomType;
        } else if ((room as any).category) {
          roomTypeValue = (room as any).category;
        } else if ((room as any).label) {
          roomTypeValue = (room as any).label;
        } else if (room.name) {
          // Use name as fallback and try to extract room type from it
          const name = room.name.toLowerCase();
          if (name.includes("kitchen")) roomTypeValue = "kitchen";
          else if (name.includes("bath") || name.includes("toilet"))
            roomTypeValue = "bathroom";
          else if (name.includes("living") || name.includes("hall"))
            roomTypeValue = "living";
          else if (name.includes("bed")) roomTypeValue = "bedroom";
          else if (name.includes("dining")) roomTypeValue = "dining";
          else roomTypeValue = name;
        } else {
          roomTypeValue = "bedroom";
        }

        const rawType = roomTypeValue.toLowerCase();
        console.log(`Room ${room.name || room.id} - detected type:`, rawType);

        // Simple image selection
        let imgSrc = bedroomImg; // default

        if (rawType.includes("kitchen") || rawType.includes("cooking")) {
          imgSrc = kitchenImg;
        } else if (
          rawType.includes("toilet") ||
          rawType.includes("bath") ||
          rawType.includes("wash")
        ) {
          imgSrc = toiletImg;
        } else if (
          rawType.includes("living") ||
          rawType.includes("hall") ||
          rawType.includes("lounge") ||
          rawType.includes("sitting")
        ) {
          imgSrc = livingRoomImg;
        } else if (
          rawType.includes("bed") ||
          rawType.includes("master") ||
          rawType.includes("sleep") ||
          (rawType.includes("room") &&
            !rawType.includes("living") &&
            !rawType.includes("bath"))
        ) {
          imgSrc = bedroomImg;
        } else if (rawType.includes("dining") || rawType.includes("eat")) {
          imgSrc = livingRoomImg; // Change this if you have a dining image
        }

        console.log(
          `Room ${room.name || room.id} - assigned image:`,
          imgSrc === bedroomImg ? "bedroom" : "matched",
        );

        const imgSize = 120;

        svg
          .select(".floorplan-group")
          .append("image")
          .attr("href", imgSrc)
          .attr("x", centerX - imgSize / 2)
          .attr("y", centerY - imgSize / 2)
          .attr("width", imgSize)
          .attr("height", imgSize)
          .attr("opacity", 0.95);
      });
    }

    const drag = d3
      .drag<SVGSVGElement, unknown>()
      .on("start", () => {
        svg.on(".zoom", null);
      })
      .on("drag", (event) => {
        zoomTransform.current = d3.zoomTransform(
          svgRef.current as SVGSVGElement,
        );
        mainGroup.attr("transform", () => {
          return `translate(${zoomTransform.current.x + event.dx},${
            zoomTransform.current.y + event.dy
          }) scale(${zoomTransform.current.k})`;
        });
      })
      .on("end", () => {
        svg.call(zoomBehavior);
      });
    // Helper function to safely get viewport dimensions

    // Updated drawWallDimensions function
    const drawWallDimensions = (
      contentGroup: d3.Selection<SVGGElement | null, unknown, null, undefined>,
      x1: number,
      y1: number,
      x2: number,
      y2: number,
      width: number,
      height: number,
      minXWall: number, // Add minXWall parameter
      minYWall: number, // Add minYWall parameter
      maxYWall: number, // Add maxYWall parameter
    ): void => {
      // Normalize coordinates relative to the floorplan's bottom-left (0,0)
      const normalizedX1 = x1 - minXWall;
      const normalizedY1 = maxYWall - y1; // Corrected for top-left 0,0
      const normalizedX2 = x2 - minXWall;
      const normalizedY2 = maxYWall - y2; // Corrected for top-left 0,0

      const wallLength = calculateUnscaledWallLength(
        x1,
        y1,
        x2,
        y2,
        width,
        height,
      );
      const dimensionText =
        unit === "meters"
          ? convertUnitsToMeters(wallLength)
          : convertUnitsToFeetAndInches(wallLength);

      // Display only the dimension text
      // const coordinatesText = `(${normalizedX1.toFixed(0)},${normalizedY1.toFixed(0)}) to (${normalizedX2.toFixed(0)},${normalizedY2.toFixed(0)})`;
      // const combinedText = `${coordinatesText} - ${dimensionText}`;

      // Calculate midpoint for placing the dimension text
      const midX = (x1 + x2) / 2;
      const midY = (y1 + y2) / 2;

      // Adjust the text position slightly to avoid overlap with the wall
      const offset = 10; // Adjust this value based on your design

      const dx = x2 - x1;
      const dy = y2 - y1;
      const wallAngle = Math.atan2(dy, dx);
      const textX = midX + offset * Math.cos(wallAngle + Math.PI / 2);
      const textY = midY + offset * Math.sin(wallAngle + Math.PI / 2);

      // Add the dimension text
      contentGroup
        .append("text")
        .attr("x", textX)
        .attr("y", textY)
        .attr("fill", "#000")
        .attr("font-size", "12px")
        .attr("font-weight", "bold")
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")

        .attr(
          "transform",
          `rotate(${(wallAngle * 180) / Math.PI}, ${textX}, ${textY})`,
        )

        .text(dimensionText);
    };

    let minXWall = 1e9;
    let maxXWall = -1e9;
    let minYWall = 1e9;
    let maxYWall = -1e9;

    walls?.forEach((wall) => {
      minXWall = Math.min(minXWall, wall.x1, wall.x2);
      minYWall = Math.min(minYWall, wall.y1, wall.y2);
      maxXWall = Math.max(maxXWall, wall.x1, wall.x2);
      maxYWall = Math.max(maxYWall, wall.y1, wall.y2);
    });

    // Ensure floorplanWidth and floorplanHeight are used for drawWallDimensions consistently
    const currentFloorplanWidth = maxXWall - minXWall;
    const currentFloorplanHeight = maxYWall - minYWall;

    svg.call(drag);
    walls?.forEach((wall) => {
      const room = rooms?.find((r) => wall?.roomIds?.includes(r.id));
      drawLine(
        wall.x1,
        wall.y1,
        wall.x2,
        wall.y2,
        false,
        //@ts-ignore
        d3.select(contentGroupRef.current),
        wall.id,
        false,
      );

      drawWallDimensions(
        d3.select(contentGroupRef.current),
        wall.x1,
        wall.y1,
        wall.x2,
        wall.y2,
        floorplan_width,
        floorplan_height,
        minXWall,
        minYWall,
        maxYWall,
      );
    });

    // Draw room images BEFORE labels (so labels appear on top)
    if (selectedTemplate === "housing") {
      rooms.forEach((room) => {
        if (!room || !room.walls || room.walls.length === 0) return;

        let minX = Infinity,
          minY = Infinity,
          maxX = -Infinity,
          maxY = -Infinity;
        room.walls.forEach((wall) => {
          minX = Math.min(minX, wall.x1, wall.x2);
          minY = Math.min(minY, wall.y1, wall.y2);
          maxX = Math.max(maxX, wall.x1, wall.x2);
          maxY = Math.max(maxY, wall.y1, wall.y2);
        });

        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        const rawType = (
          (room as any).roomtype ||
          (room as any).original_name ||
          room.name ||
          ""
        ).toLowerCase();

        // Calculate actual room dimensions
        const roomWidth = maxX - minX;
        const roomHeight = maxY - minY;

        // Use smaller of width/height to keep image square and inside room
        // Subtract padding to keep image away from walls
        const padding = 10;
        const imgSize = Math.min(roomWidth, roomHeight) - padding;

        let imgSrc = bedroomImg;

        if (rawType.includes("kitchen")) {
          imgSrc = kitchenImg;
        } else if (rawType.includes("bath") || rawType.includes("toilet")) {
          imgSrc = toiletImg;
        } else if (rawType.includes("living") || rawType.includes("hall")) {
          imgSrc = livingRoomImg;
        } else if (rawType.includes("bed")) {
          imgSrc = bedroomImg;
        } else if (rawType.includes("office") || rawType.includes("study")) {
          imgSrc = officeImg;
        } else if (rawType.includes("dining") || rawType.includes("eat")) {
          imgSrc = diningImg;
        }

        d3.select(contentGroupRef.current)
          .append("image")
          .attr("href", imgSrc)
          .attr("x", centerX - imgSize / 2)
          .attr("y", centerY - imgSize / 2)
          .attr("width", imgSize)
          .attr("height", imgSize)
          .attr("opacity", 0.85)
          .attr("pointer-events", "none");
      });
    }

    // Draw labels AFTER images (so they appear on top)
    labels?.forEach((label) => {
      const room = rooms?.find((r) => String(r.id) === String(label.roomId));
      let labelX = label.x;
      let labelY = label.y;

      if (room && room.walls && room.walls.length > 0) {
        let minX = Number.MAX_VALUE;
        let maxX = Number.MIN_VALUE;
        let minY = Number.MAX_VALUE;
        let maxY = Number.MIN_VALUE;

        room.walls.forEach((w) => {
          minX = Math.min(minX, w.x1, w.x2);
          maxX = Math.max(maxX, w.x1, w.x2);
          minY = Math.min(minY, w.y1, w.y2);
          maxY = Math.max(maxY, w.y1, w.y2);
        });

        if (minX !== Number.MAX_VALUE && maxX !== Number.MIN_VALUE) {
          labelX = (minX + maxX) / 2;
          labelY = (minY + maxY) / 2;
        }
      }

      addRoomLabel(labelX, labelY, label.text, label.roomId);
    });

    const findWallForAsset = (
      assetX: number,
      assetY: number,
      wallsArray: Wall[],
    ): Wall | undefined => {
      // Tolerance for floating point comparisons
      const tolerance = 5; // Pixels
      for (const wall of wallsArray) {
        const { x1, y1, x2, y2 } = wall;

        // Check if the asset's coordinates are within the bounding box of the wall
        const minXBound = Math.min(x1, x2) - tolerance;
        const maxXBound = Math.max(x1, x2) + tolerance;
        const minYBound = Math.min(y1, y2) - tolerance;
        const maxYBound = Math.max(y1, y2) + tolerance;

        if (
          assetX >= minXBound &&
          assetX <= maxXBound &&
          assetY >= minYBound &&
          assetY <= maxYBound
        ) {
          // A simplified approach for collinearity on horizontal/vertical lines within tolerance
          if (x1 === x2) {
            // Vertical wall
            if (Math.abs(assetX - x1) < tolerance) {
              // AssetX is close to wallX

              return wall;
            }
          } else if (y1 === y2) {
            // Horizontal wall
            if (Math.abs(assetY - y1) < tolerance) {
              // AssetY is close to wallY

              return wall;
            }
          } else {
            // Diagonal wall - use cross product (more robust for general lines)
            const crossProduct =
              (assetY - y1) * (x2 - x1) - (assetX - x1) * (y2 - y1);
            if (Math.abs(crossProduct) < tolerance * 10) {
              // Increased tolerance for collinearity check

              return wall;
            }
          }
        }
      }

      return undefined;
    };

    const distanceToRoomBounds = (
      room: Room,
      px: number,
      py: number,
    ): number => {
      if (!room.walls || room.walls.length === 0) return Number.MAX_VALUE;

      const xs: number[] = [];
      const ys: number[] = [];
      room.walls.forEach((w) => {
        xs.push(w.x1, w.x2);
        ys.push(w.y1, w.y2);
      });

      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);

      const dx = px < minX ? minX - px : px > maxX ? px - maxX : 0;
      const dy = py < minY ? minY - py : py > maxY ? py - maxY : 0;

      return Math.hypot(dx, dy);
    };

    const getNearestRoomDistance = (px: number, py: number): number => {
      if (!rooms || rooms.length === 0) return Number.MAX_VALUE;
      let minDist = Number.MAX_VALUE;
      rooms.forEach((room) => {
        const dist = distanceToRoomBounds(room, px, py);
        if (dist < minDist) minDist = dist;
      });
      return minDist;
    };

    const getDoorSwingSign = (
      x: number,
      y: number,
      isVertical: boolean,
      wall: Wall,
    ): 1 | -1 => {
      const probeOffset = 18;
      const epsilon = 0.001;
      if (isVertical) {
        const leftDist = getNearestRoomDistance(wall.x1 - probeOffset, y);
        const rightDist = getNearestRoomDistance(wall.x1 + probeOffset, y);
        if (Math.abs(leftDist - rightDist) < epsilon) return 1;
        return leftDist < rightDist ? -1 : 1;
      }
      const upDist = getNearestRoomDistance(x, wall.y1 - probeOffset);
      const downDist = getNearestRoomDistance(x, wall.y1 + probeOffset);
      if (Math.abs(upDist - downDist) < epsilon) return 1;
      return upDist < downDist ? -1 : 1;
    };

    // Draw door assets
    if (contentGroupRef.current) {
      // Add null check
      const currentContentGroup = d3.select(
        contentGroupRef.current,
      ) as d3.Selection<SVGGElement, unknown, null, undefined>;
      doorAssets?.forEach((asset) => {
        const correspondingWall = findWallForAsset(
          asset.x,
          asset.y,
          walls || [],
        );
        if (correspondingWall) {
          addAsset(
            currentFloorplanWidth,
            currentFloorplanHeight,
            currentContentGroup,
            asset,
            correspondingWall,
            "door",
          );
        } else {
          console.warn(
            `Door Asset with id ${asset.id} (x:${asset.x}, y:${asset.y}) has no corresponding wall. Skipping.`,
          );
        }
      });
    }

    // Draw window assets
    if (contentGroupRef.current) {
      // Add null check
      const currentContentGroup = d3.select(
        contentGroupRef.current,
      ) as d3.Selection<SVGGElement, unknown, null, undefined>;
      windowAssets?.forEach((asset) => {
        const correspondingWall = findWallForAsset(
          asset.x,
          asset.y,
          walls || [],
        );
        if (correspondingWall) {
          addAsset(
            currentFloorplanWidth,
            currentFloorplanHeight,
            currentContentGroup,
            asset,
            correspondingWall,
            "window",
          );
        } else {
          console.warn(
            `Window Asset with id ${asset.id} (x:${asset.x}, y:${asset.y}) has no corresponding wall. Skipping.`,
          );
        }
      });
    }

    if (showPlot) {
      plot_boundary?.forEach((boundary) => {
        drawLine(
          boundary.x1,
          boundary.y1,
          boundary.x2,
          boundary.y2,
          false,
          //@ts-ignore
          d3.select(contentGroupRef.current),
          boundary.id,
          true,
        );

        drawWallDimensions(
          d3.select(contentGroupRef.current),
          boundary.x1,
          boundary.y1,
          boundary.x2,
          boundary.y2,
          floorplan_width, // Use currentFloorplanWidth
          floorplan_height, // Use currentFloorplanHeight
          minXWall, // Pass minXWall
          minYWall, // Pass minYWall
          maxYWall, // Pass maxYWall
        );
      });
    }

    dispatch(setCleanUpDone(false));
    dispatch(setCleanUpRequired(false));
  }, [cleanUpDone, dimensioned, fromDocuments, unit, showPlot, plot_boundary]);
  useEffect(() => {
    dispatch(setCleanUpDone(true));
  }, [dispatch, unit]);
  useEffect(() => {
    // console.log("walls", walls);
  }, [walls, unit]);
  useEffect(() => {
    // console.log("rooms", rooms);
  }, [rooms]);

  //@ts-ignore

  const lineDrag: d3.DragBehavior<Element, unknown, unknown> = d3
    .drag<SVGLineElement, unknown>()
    .on("start", function (event) {
      const currentActiveTool = activeToolRef.current;
      const currentDraw = drawRef.current;
      const currentWalls = wallsRef.current;
      const currentPlotBoundary = plotBoundaryRef.current;

      if (
        selectedRef.current &&
        !currentDraw &&
        (currentActiveTool === "select" ||
          currentActiveTool === "arrow" ||
          currentActiveTool === null)
      ) {
        d3.select(this).raise();
        // @ts-ignore
        this.dragStartTime = new Date().getTime();
        const line = d3.select(this);
        const wall = currentWalls?.find((wall) => wall.id === line.attr("id"));
        const plot_wall = currentPlotBoundary?.find(
          (wall) => wall.id === line.attr("id"),
        );
        if (
          wall === selectedWallRef.current ||
          plot_wall === selectedWallRef.current
        )
          line.attr("stroke", "red");
      }
    })
    .on("drag", function (event) {
      const currentActiveTool = activeToolRef.current;
      const currentDraw = drawRef.current;
      const currentWalls = wallsRef.current;
      const currentRooms = roomsRef.current;
      const currentPlotBoundary = plotBoundaryRef.current;

      if (
        selectedRef.current &&
        !currentDraw &&
        (currentActiveTool === "select" ||
          currentActiveTool === "arrow" ||
          currentActiveTool === null)
      ) {
        const line = d3.select(this);
        const wall = currentWalls?.find((wall) => wall.id === line.attr("id"));
        const plot_wall = currentPlotBoundary?.find(
          (wall) => wall.id === line.attr("id"),
        );
        if (
          wall === selectedWallRef.current ||
          plot_wall === selectedWallRef.current
        ) {
          let minX: number = 0,
            maxX: number = 0,
            minY: number = 0,
            maxY: number = 0;
          // wall limits
          if (wall && currentRooms) {
            const a = getWallLimits(
              wall,
              currentRooms,
              currentPlotBoundary,
              showPlot,
            );
            minX = a.minX;
            minY = a.minY;
            maxX = a.maxX;
            maxY = a.maxY;
          }
          // plot boundary limits
          else if (plot_wall && currentRooms) {
            const a = getFloorplanLimits(currentRooms);
            minX = plot_wall.x1 >= a.maxFPX ? a.maxFPX : Number.MIN_VALUE;
            maxX = plot_wall.x1 <= a.minFPX ? a.minFPX : Number.MAX_VALUE;
            minY = plot_wall.y1 >= a.maxFPY ? a.maxFPY : Number.MIN_VALUE;
            maxY = plot_wall.y1 <= a.minFPY ? a.minFPY : Number.MAX_VALUE;
            minX += 5;
            maxX -= 5;
            minY += 5;
            maxY -= 5;
          }
          const dx = event.dx,
            dy = event.dy;
          // vertical line constrained to move horizontally
          if (line.attr("x1") === line.attr("x2")) {
            const newX = Math.min(
              Math.max(parseFloat(line.attr("x1")) + dx, minX),
              maxX,
            );
            line.attr("x1", newX).attr("x2", newX);
          }
          // horizontal line constrained to move vertically
          else {
            const newY = Math.min(
              Math.max(parseFloat(line.attr("y1")) + dy, minY),
              maxY,
            );
            line.attr("y1", newY).attr("y2", newY);
          }
        }
      }
    })
    .on("end", function (event, d) {
      const currentActiveTool = activeToolRef.current;
      const currentDraw = drawRef.current;
      const currentWalls = wallsRef.current;
      const currentRooms = roomsRef.current;
      const currentPlotBoundary = plotBoundaryRef.current;

      if (selectedRef.current && !currentDraw) {
        const line = d3.select(this);
        const lineId = line.attr("id");
        const dragEndTime = new Date().getTime();
        // @ts-ignore
        const dragDuration = dragEndTime - this.dragStartTime;
        if (dragDuration < 250) {
          // Consider it a click if the drag lasted less than 200ms
          const [cx, cy] = d3.pointer(event, contentGroupRef.current);
          handleLineClick(lineId, cx, cy);
        } else if (
          currentActiveTool === "select" ||
          currentActiveTool === "arrow" ||
          currentActiveTool === null
        ) {
          // Only update wall positions if in select or arrow mode
          const newPositions = {
            id: lineId,
            x1: parseFloat(line.attr("x1")),
            y1: parseFloat(line.attr("y1")),
            x2: parseFloat(line.attr("x2")),
            y2: parseFloat(line.attr("y2")),
          };
          const wall = currentWalls?.find((w) => w.id === lineId);
          if (wall) {
            const updatedWall: Wall = {
              ...wall,
              x1: newPositions.x1,
              x2: newPositions.x2,
              y1: newPositions.y1,
              y2: newPositions.y2,
            };
            let obj = {
              updatedWalls: currentWalls,
              updatedRooms: currentRooms,
            };
            updateWall(wall, updatedWall, obj);
            updateWalls(wall, updatedWall, obj);
            // Lock rooms only when modifying a regular wall (not plot boundary)
            dispatch(setLockRooms(true));
            dispatch(setWallTransformationUsed());

            // Force redraw so rooms resize immediately
            dispatch(setCleanUpDone(false));
            setTimeout(() => dispatch(setCleanUpDone(true)), 10);
          }

          const plot_wall = currentPlotBoundary?.find(
            (wall) => wall.id === line.attr("id"),
          );
          if (plot_wall && currentPlotBoundary) {
            const updatedPlotWall: Wall = {
              ...plot_wall,
              x1: newPositions.x1,
              x2: newPositions.x2,
              y1: newPositions.y1,
              y2: newPositions.y2,
            };
            updatePlotBoundary(plot_wall, updatedPlotWall);
          }
        }
      }

      // Only clear selection option if it's not select or move anymore
      if (
        currentActiveTool !== "select" &&
        currentActiveTool !== "arrow" &&
        currentActiveTool !== null
      ) {
        dispatch(setSelectedWallOption(null));
      }
    });

  // delete wall on double click
  // d3.selectAll("line").on("dblclick", function (event) {
  //   const line = d3.select(this);
  //   line.remove();
  //   const wall: Wall | undefined = walls?.find((w) => w.id === line.attr("id"));
  //   if (wall) deleteWall(wall);
  // });

  const deleteWall = (wall: Wall) => {
    dispatch(clearRedoStacks());
    dispatch(
      pushUndoStacks({ rooms: rooms, walls: walls, plot: plot_boundary }),
    );
    if (wall.roomIds.length > 0) {
      let updatedRooms: Room[] = [];
      let updatedWalls: Wall[];
      // max two rooms will share a common wall
      let idx1: number = -1,
        idx2: number = -1;
      if (walls) {
        updatedWalls = [];
        if (rooms) {
          rooms?.forEach((room) => {
            if (wall?.roomIds.includes(room.id)) {
              if (idx1 === -1) idx1 = rooms.indexOf(room);
              else if (idx2 === -1) idx2 = rooms.indexOf(room);
            }
          });

          let tempWalls: Wall[] = walls;

          // merge the walls of room1 and room2 if they can form a larger wall
          if (idx2 > 0) {
            rooms[idx1]?.walls?.forEach((w1) => {
              let w1Horizontal: boolean = w1.y1 === w1.y2;
              rooms[idx2]?.walls?.forEach((w2) => {
                // only merge if these walls are not common to other rooms, as in that case they must remain split
                if (
                  w1 !== w2 &&
                  ((w1.roomIds.length === 1 && w2.roomIds.length === 1) ||
                    (w1.roomIds.includes(rooms[idx2].id) &&
                      w2.roomIds.includes(rooms[idx1].id)))
                ) {
                  let w2Horizontal: boolean = w2.y1 === w2.y2;
                  if (w1Horizontal === w2Horizontal) {
                    if (w1Horizontal) {
                      // check if they are adjacent walls and can be merged into one
                      if (
                        w1.y1 === w2.y1 &&
                        Math.abs(w1.x1 - w1.x2) + Math.abs(w2.x1 - w2.x2) ===
                          Math.max(w1.x1, w1.x2, w2.x1, w2.x2) -
                            Math.min(w1.x1, w1.x2, w2.x1, w2.x2)
                      ) {
                        tempWalls = tempWalls.filter(
                          (w) => w !== w1 && w !== w2,
                        );
                        let newWall: Wall = {
                          ...w1,
                          x1: Math.min(w1.x1, w1.x2, w2.x1, w2.x2),
                          x2: Math.max(w1.x1, w1.x2, w2.x1, w2.x2),
                        };
                        tempWalls.push(newWall);
                      }
                    } else {
                      if (
                        w1.x1 === w2.x1 &&
                        Math.abs(w1.y1 - w1.y2) + Math.abs(w2.y1 - w2.y2) ===
                          Math.max(w1.y1, w1.y2, w2.y1, w2.y2) -
                            Math.min(w1.y1, w1.y2, w2.y1, w2.y2)
                      ) {
                        tempWalls = tempWalls.filter(
                          (w) => w !== w1 && w !== w2,
                        );
                        let newWall: Wall = {
                          ...w1,
                          y1: Math.min(w1.y1, w1.y2, w2.y1, w2.y2),
                          y2: Math.max(w1.y1, w1.y2, w2.y1, w2.y2),
                        };
                        tempWalls.push(newWall);
                      }
                    }
                  }
                }
              });
            });
          }

          // if two rooms share the wall, then merge both the rooms
          tempWalls?.forEach((w) => {
            if (wall.id !== w.id) {
              let updatedRoomIds: string[] = w.roomIds;
              if (idx2 > 0 && w.roomIds.includes(rooms[idx2].id)) {
                updatedRoomIds = w.roomIds.filter(
                  (rid) => rid !== rooms[idx2].id,
                );
                if (!w.roomIds.includes(rooms[idx1].id)) {
                  updatedRoomIds.push(rooms[idx1].id);
                }
              }
              w = { ...w, roomIds: updatedRoomIds };
              updatedWalls.push(w);
            }
          });

          rooms?.forEach((room) => {
            if ((idx2 > 0 && rooms[idx2].id !== room.id) || idx2 === -1) {
              let roomWalls: Wall[] = updatedWalls.filter((w) =>
                w.roomIds.includes(room.id),
              );
              room = { ...room, walls: roomWalls };
              updatedRooms.push(room);
            }
          });

          const updatedLabels = recalculateLabels(updatedRooms, labels);
          dispatch(setWalls(updatedWalls));
          dispatch(setLabels(updatedLabels));
          dispatch(setRooms(updatedRooms));
          dispatch(setCleanUpDone(false));
          setTimeout(() => dispatch(setCleanUpDone(true)), 10);
          dispatch(setSelectedWall(undefined));
        }
      }
    } else {
      if (walls) {
        let updatedWalls: Wall[] = walls.filter((w) => w !== wall);
        dispatch(setWalls(updatedWalls));
      }
      if (plot_boundary) {
        let updatedPlotBoundary: Wall[] = plot_boundary?.filter(
          (w) => w !== wall,
        );
        dispatch(setPlot(updatedPlotBoundary));
      }
      dispatch(setCleanUpDone(false));
      setTimeout(() => dispatch(setCleanUpDone(true)), 10);
      dispatch(setSelectedWall(undefined));
    }
  };

  const updateRoom = (
    oldWall: Wall,
    newWall: Wall,
    obj: any,
    remove: boolean,
  ) => {
    let updatedWalls: Wall[] = obj.updatedWalls;
    if (remove) {
      updatedWalls = updatedWalls.filter((w) => w !== oldWall);
    }
    obj.updatedRooms = [];
    rooms?.forEach((room) => {
      let roomWalls: Wall[] = updatedWalls.filter(
        (w) => w.roomIds && w.roomIds.includes(room.id),
      );
      room = { ...room, walls: roomWalls };
      obj.updatedRooms.push(room);
    });
  };

  const updateWall = (oldWall: Wall, newWall: Wall, obj: any) => {
    obj.updatedWalls = obj.updatedWalls.filter((w: any) => w.id !== oldWall.id);
    obj.updatedWalls.push(newWall);
    updateRoom(oldWall, newWall, obj, true);
  };

  const updateWalls = (oldWall: Wall, newWall: Wall, obj: any) => {
    dispatch(clearRedoStacks());
    dispatch(
      pushUndoStacks({ rooms: rooms, walls: walls, plot: plot_boundary }),
    );
    walls?.forEach((wall) => {
      if (wall !== oldWall) {
        let oldwallHorizontal: boolean = oldWall.y1 === oldWall.y2;
        let wallHorizontal: boolean = wall.y1 === wall.y2;
        let arePerpendicular: boolean = oldwallHorizontal !== wallHorizontal;
        let oldwallUpwards: boolean =
          oldWall.y1 <= wall.y1 && oldWall.y1 <= wall.y2;
        let oldwallLeftwards: boolean =
          oldWall.x1 <= wall.x1 && oldWall.x1 <= wall.x2;

        // check for perpendicular walls
        if (arePerpendicular) {
          let intersect: boolean = false;
          let flag: boolean = false;

          let x: number, y: number;
          if (
            (wall.x1 === oldWall.x1 && wall.y1 === oldWall.y1) ||
            (wall.x1 === oldWall.x2 && wall.y1 === oldWall.y2)
          ) {
            x = wall.x1;
            y = wall.y1;
            intersect = true;
          } else if (
            (wall.x2 === oldWall.x1 && wall.y2 === oldWall.y1) ||
            (wall.x2 === oldWall.x2 && wall.y2 === oldWall.y2)
          ) {
            x = wall.x2;
            y = wall.y2;
            intersect = true;
          }
          if (!intersect) return;

          // ------------------------------ REPLACE WITH MAP --------------------------------

          // check if there is a third wall (perpendicular to wall) apart from oldwall and wall, with the same intersection point
          // if there is not, then just update wall coordinates with newwall coordinates, and do not create a "new" wall
          walls?.forEach((w) => {
            if (w !== oldWall && w !== wall) {
              let wHorizontal: boolean = w.y1 === w.y2;
              if (
                wallHorizontal !== wHorizontal &&
                ((w.x1 === x && w.y1 === y) || (w.x2 === x && w.y2 === y))
              )
                flag = true;
            }
          });
          // -------------------------------------------------------------------------------

          if (oldwallHorizontal) {
            let mnY: number = Number.MIN_VALUE,
              mxY: number = Number.MAX_VALUE;
            (wall.roomIds ?? []).forEach((rid) => {
              let room = rooms?.find((r) => r.id === rid);
              room?.walls?.forEach((w) => {
                // if w is in continuation with wall and intersects with oldwall
                if (
                  w !== wall &&
                  w.x1 === w.x2 &&
                  w.x1 === wall.x1 &&
                  Math.abs(w.y1 - oldWall.y1) + Math.abs(w.y2 - oldWall.y1) ===
                    Math.abs(w.y1 - w.y2)
                ) {
                  if (
                    Math.abs(w.y1 - w.y2) + Math.abs(wall.y1 - wall.y2) ===
                    Math.max(w.y1, w.y2, wall.y1, wall.y2) -
                      Math.min(w.y1, w.y2, wall.y1, wall.y2)
                  ) {
                    mnY = Math.min(w.y1, w.y2);
                    mxY = Math.max(w.y1, w.y2);
                  }
                }
                // if w is in continuation with wall but doesn't intersect with oldwall
                else if (
                  w !== wall &&
                  w.x1 === w.x2 &&
                  w.x1 === wall.x1 &&
                  ((oldwallUpwards && newWall.y1 > oldWall.y1) ||
                    (!oldwallUpwards && newWall.y1 < oldWall.y1))
                ) {
                  if (
                    Math.abs(w.y1 - w.y2) + Math.abs(wall.y1 - wall.y2) ===
                    Math.max(w.y1, w.y2, wall.y1, wall.y2) -
                      Math.min(w.y1, w.y2, wall.y1, wall.y2)
                  ) {
                    mnY = Math.min(w.y1, w.y2);
                    mxY = Math.max(w.y1, w.y2);
                    const updatedWall: Wall = {
                      ...w,
                      y1: oldwallUpwards
                        ? Math.max(w.y1, w.y2)
                        : Math.min(w.y1, w.y2),
                      y2: oldwallUpwards
                        ? Math.max(mnY, newWall.y1)
                        : Math.min(mxY, newWall.y1),
                    };
                    updateWall(w, updatedWall, obj);
                  }
                }
              });
            });

            // nested ternary operators check if the wall crosses some other wall then the updated wall should not overlap with the wall being crossed
            if (wall.x1 === oldWall.x1 && wall.y1 === oldWall.y1) {
              const updatedWall: Wall = {
                ...wall,
                y1:
                  !flag ||
                  (oldwallUpwards && newWall.y1 > oldWall.y1) ||
                  (!oldwallUpwards && newWall.y1 < oldWall.y1)
                    ? !flag && oldwallUpwards && newWall.y1 < oldWall.y1
                      ? Math.max(newWall.y1, mnY)
                      : !flag && !oldwallUpwards && newWall.y1 > oldWall.y1
                        ? Math.min(newWall.y1, mxY)
                        : newWall.y1
                    : oldWall.y1,
              };
              updateWall(wall, updatedWall, obj);
            } else if (wall.x2 === oldWall.x1 && wall.y2 === oldWall.y1) {
              const updatedWall: Wall = {
                ...wall,
                y2:
                  !flag ||
                  (oldwallUpwards && newWall.y1 > oldWall.y1) ||
                  (!oldwallUpwards && newWall.y1 < oldWall.y1)
                    ? !flag && oldwallUpwards && newWall.y1 < oldWall.y1
                      ? Math.max(newWall.y1, mnY)
                      : !flag && !oldwallUpwards && newWall.y1 > oldWall.y1
                        ? Math.min(newWall.y1, mxY)
                        : newWall.y1
                    : oldWall.y1,
              };
              updateWall(wall, updatedWall, obj);
            } else if (wall.x1 === oldWall.x2 && wall.y1 === oldWall.y2) {
              const updatedWall: Wall = {
                ...wall,
                y1:
                  !flag ||
                  (oldwallUpwards && newWall.y2 > oldWall.y2) ||
                  (!oldwallUpwards && newWall.y2 < oldWall.y2)
                    ? !flag && oldwallUpwards && newWall.y2 < oldWall.y2
                      ? Math.max(newWall.y2, mnY)
                      : !flag && !oldwallUpwards && newWall.y2 > oldWall.y2
                        ? Math.min(newWall.y2, mxY)
                        : newWall.y2
                    : oldWall.y2,
              };
              updateWall(wall, updatedWall, obj);
            } else if (wall.x2 === oldWall.x2 && wall.y2 === oldWall.y2) {
              const updatedWall: Wall = {
                ...wall,
                y2:
                  !flag ||
                  (oldwallUpwards && newWall.y2 > oldWall.y2) ||
                  (!oldwallUpwards && newWall.y2 < oldWall.y2)
                    ? !flag && oldwallUpwards && newWall.y2 < oldWall.y2
                      ? Math.max(newWall.y2, mnY)
                      : !flag && !oldwallUpwards && newWall.y2 > oldWall.y2
                        ? Math.min(newWall.y2, mxY)
                        : newWall.y2
                    : oldWall.y2,
              };
              updateWall(wall, updatedWall, obj);
            }
          } else {
            let mnX: number = Number.MIN_VALUE,
              mxX: number = Number.MAX_VALUE;
            wall.roomIds.forEach((rid) => {
              let room = rooms?.find((r) => r.id === rid);
              room?.walls?.forEach((w) => {
                // if w is in continuation with wall and intersects with oldwall
                if (
                  w !== wall &&
                  w.y1 === w.y2 &&
                  w.y1 === wall.y1 &&
                  Math.abs(w.x1 - oldWall.x1) + Math.abs(w.x2 - oldWall.x1) ===
                    Math.abs(w.x1 - w.x2)
                ) {
                  if (
                    Math.abs(w.x1 - w.x2) + Math.abs(wall.x1 - wall.x2) ===
                    Math.max(w.x1, w.x2, wall.x1, wall.x2) -
                      Math.min(w.x1, w.x2, wall.x1, wall.x2)
                  ) {
                    mnX = Math.min(w.x1, w.x2);
                    mxX = Math.max(w.x1, w.x2);
                  }
                }
                // if w is in continuation with wall but doesn't intersect with oldwall
                else if (
                  w !== wall &&
                  w.y1 === w.y2 &&
                  w.y1 === wall.y1 &&
                  ((oldwallLeftwards && newWall.x1 > oldWall.x1) ||
                    (!oldwallLeftwards && newWall.x1 < oldWall.x1))
                ) {
                  if (
                    Math.abs(w.x1 - w.x2) + Math.abs(wall.x1 - wall.x2) ===
                    Math.max(w.x1, w.x2, wall.x1, wall.x2) -
                      Math.min(w.x1, w.x2, wall.x1, wall.x2)
                  ) {
                    mnX = Math.min(w.x1, w.x2);
                    mxX = Math.max(w.x1, w.x2);
                    const updatedWall: Wall = {
                      ...w,
                      x1: oldwallLeftwards
                        ? Math.max(w.x1, w.x2)
                        : Math.min(w.x1, w.x2),
                      x2: oldwallLeftwards
                        ? Math.max(mnX, newWall.x1)
                        : Math.min(mxX, newWall.x1),
                    };
                    updateWall(w, updatedWall, obj);
                  }
                }
              });
            });

            if (wall.x1 === oldWall.x1 && wall.y1 === oldWall.y1) {
              const updatedWall: Wall = {
                ...wall,
                x1:
                  !flag ||
                  (oldwallLeftwards && newWall.x1 > oldWall.x1) ||
                  (!oldwallLeftwards && newWall.x1 < oldWall.x1)
                    ? !flag && oldwallLeftwards && newWall.x1 < oldWall.x1
                      ? Math.max(newWall.x1, mnX)
                      : !flag && !oldwallLeftwards && newWall.x1 > oldWall.x1
                        ? Math.min(newWall.x1, mxX)
                        : newWall.x1
                    : oldWall.x1,
              };
              updateWall(wall, updatedWall, obj);
            } else if (wall.x2 === oldWall.x1 && wall.y2 === oldWall.y1) {
              const updatedWall: Wall = {
                ...wall,
                x2:
                  !flag ||
                  (oldwallLeftwards && newWall.x1 > oldWall.x1) ||
                  (!oldwallLeftwards && newWall.x1 < oldWall.x1)
                    ? !flag && oldwallLeftwards && newWall.x1 < oldWall.x1
                      ? Math.max(newWall.x1, mnX)
                      : !flag && !oldwallLeftwards && newWall.x1 > oldWall.x1
                        ? Math.min(newWall.x1, mxX)
                        : newWall.x1
                    : oldWall.x1,
              };
              updateWall(wall, updatedWall, obj);
            } else if (wall.x1 === oldWall.x2 && wall.y1 === oldWall.y2) {
              const updatedWall: Wall = {
                ...wall,
                x1:
                  !flag ||
                  (oldwallLeftwards && newWall.x2 > oldWall.x2) ||
                  (!oldwallLeftwards && newWall.x2 < oldWall.x2)
                    ? !flag && oldwallLeftwards && newWall.x2 < oldWall.x2
                      ? Math.max(newWall.x2, mnX)
                      : !flag && !oldwallLeftwards && newWall.x2 > oldWall.x2
                        ? Math.min(newWall.x2, mxX)
                        : newWall.x2
                    : oldWall.x2,
              };
              updateWall(wall, updatedWall, obj);
            } else if (wall.x2 === oldWall.x2 && wall.y2 === oldWall.y2) {
              const updatedWall: Wall = {
                ...wall,
                x2:
                  !flag ||
                  (oldwallLeftwards && newWall.x2 > oldWall.x2) ||
                  (!oldwallLeftwards && newWall.x2 < oldWall.x2)
                    ? !flag && oldwallLeftwards && newWall.x2 < oldWall.x2
                      ? Math.max(newWall.x2, mnX)
                      : !flag && !oldwallLeftwards && newWall.x2 > oldWall.x2
                        ? Math.min(newWall.x2, mxX)
                        : newWall.x2
                    : oldWall.x2,
              };
              updateWall(wall, updatedWall, obj);
            }
          }
        }
        // parallel
        else {
          if (
            (wall.x1 === oldWall.x1 && wall.y1 === oldWall.y1) ||
            (wall.x2 === oldWall.x1 && wall.y2 === oldWall.y1)
          ) {
            let rids: string[] = [];
            oldWall.roomIds.forEach((rid1) => {
              wall.roomIds.forEach((rid2) => {
                if (rooms) {
                  const room1: Room =
                    rooms[rooms?.findIndex((room) => room.id === rid1) || 0];
                  const room2: Room =
                    rooms[rooms?.findIndex((room) => room.id === rid2) || 0];
                  if (oldwallHorizontal) {
                    let room1Up: boolean =
                      oldWall.y1 < getRoomLimits(room1).maxY;
                    let room2Up: boolean = wall.y1 < getRoomLimits(room2).maxY;
                    if (room1Up === room2Up) {
                      if (room1Up && newWall.y1 > oldWall.y1) {
                        rids = oldWall.roomIds.filter(
                          (rid) => rid !== room1.id,
                        );
                        rids.push(room2.id);
                      } else if (room1Up) {
                        rids = wall.roomIds.filter((rid) => rid !== room2.id);
                        rids.push(room1.id);
                      } else if (!room1Up && newWall.y1 < oldWall.y1) {
                        rids = oldWall.roomIds.filter(
                          (rid) => rid !== room1.id,
                        );
                        rids.push(room2.id);
                      } else if (!room1Up) {
                        rids = wall.roomIds.filter((rid) => rid !== room2.id);
                        rids.push(room1.id);
                      }
                    }
                  } else {
                    let room1Left: boolean =
                      oldWall.x1 < getRoomLimits(room1).maxX;
                    let room2Left: boolean =
                      wall.x1 < getRoomLimits(room2).maxX;
                    if (room1Left === room2Left) {
                      if (room1Left && newWall.x1 > oldWall.x1) {
                        rids = oldWall.roomIds.filter(
                          (rid) => rid !== room1.id,
                        );
                        rids.push(room2.id);
                      } else if (room1Left) {
                        rids = wall.roomIds.filter((rid) => rid !== room2.id);
                        rids.push(room1.id);
                      } else if (!room1Left && newWall.x1 < oldWall.x1) {
                        rids = oldWall.roomIds.filter(
                          (rid) => rid !== room1.id,
                        );
                        rids.push(room2.id);
                      } else if (!room1Left) {
                        rids = wall.roomIds.filter((rid) => rid !== room2.id);
                        rids.push(room1.id);
                      }
                    }
                  }
                }
              });
            });
            const wallToAdd: Wall = {
              id: uuidv4(),
              x1: oldWall.x1,
              y1: oldWall.y1,
              x2: newWall.x1,
              y2: newWall.y1,
              roomIds: rids,
            };
            obj.updatedWalls.push(wallToAdd);
            updateRoom(wall, wallToAdd, obj, false);
          } else if (
            (wall.x1 === oldWall.x2 && wall.y1 === oldWall.y2) ||
            (wall.x2 === oldWall.x2 && wall.y2 === oldWall.y2)
          ) {
            let rids: string[] = [];
            oldWall.roomIds.forEach((rid1) => {
              wall.roomIds.forEach((rid2) => {
                if (rooms) {
                  const room1: Room =
                    rooms[rooms?.findIndex((room) => room.id === rid1) || 0];
                  const room2: Room =
                    rooms[rooms?.findIndex((room) => room.id === rid2) || 0];
                  if (oldwallHorizontal) {
                    let room1Up: boolean =
                      oldWall.y1 < getRoomLimits(room1).maxY;
                    let room2Up: boolean = wall.y1 < getRoomLimits(room2).maxY;
                    if (room1Up === room2Up) {
                      if (room1Up && newWall.y1 > oldWall.y1) {
                        rids = oldWall.roomIds.filter(
                          (rid) => rid !== room1.id,
                        );
                        rids.push(room2.id);
                      } else if (room1Up) {
                        rids = wall.roomIds.filter((rid) => rid !== room2.id);
                        rids.push(room1.id);
                      } else if (!room1Up && newWall.y1 < oldWall.y1) {
                        rids = oldWall.roomIds.filter(
                          (rid) => rid !== room1.id,
                        );
                        rids.push(room2.id);
                      } else if (!room1Up) {
                        rids = wall.roomIds.filter((rid) => rid !== room2.id);
                        rids.push(room1.id);
                      }
                    }
                  } else {
                    let room1Left: boolean =
                      oldWall.x1 < getRoomLimits(room1).maxX;
                    let room2Left: boolean =
                      wall.x1 < getRoomLimits(room2).maxX;
                    if (room1Left === room2Left) {
                      if (room1Left && newWall.x1 > oldWall.x1) {
                        rids = oldWall.roomIds.filter(
                          (rid) => rid !== room1.id,
                        );
                        rids.push(room2.id);
                      } else if (room1Left) {
                        rids = wall.roomIds.filter((rid) => rid !== room2.id);
                        rids.push(room1.id);
                      } else if (!room1Left && newWall.x1 < oldWall.x1) {
                        rids = oldWall.roomIds.filter(
                          (rid) => rid !== room1.id,
                        );
                        rids.push(room2.id);
                      } else if (!room1Left) {
                        rids = wall.roomIds.filter((rid) => rid !== room2.id);
                        rids.push(room1.id);
                      }
                    }
                  }
                }
              });
            });
            const wallToAdd: Wall = {
              id: uuidv4(),
              x1: oldWall.x2,
              y1: oldWall.y2,
              x2: newWall.x2,
              y2: newWall.y2,
              roomIds: rids,
            };
            obj.updatedWalls.push(wallToAdd);
            updateRoom(wall, wallToAdd, obj, false);
          }
        }
      }
    });
    const updatedLabels = recalculateLabels(obj.updatedRooms, labels);
    dispatch(setWalls(obj.updatedWalls));
    dispatch(setLabels(updatedLabels));
    dispatch(setRooms(obj.updatedRooms));
    dispatch(setCleanUpDone(true));
  };

  const updatePlotBoundary = (oldPlotWall: Wall, newPlotWall: Wall) => {
    dispatch(clearRedoStacks());
    dispatch(
      pushUndoStacks({ rooms: rooms, walls: walls, plot: plot_boundary }),
    );
    let updatedPlotBoundary: Wall[] = [];
    plot_boundary?.forEach((wall) => {
      let oldPlotWallHorizontal: boolean = oldPlotWall.y1 === oldPlotWall.y2;
      let wallHorizontal: boolean = wall.y1 === wall.y2;
      let arePerpendicular: boolean = oldPlotWallHorizontal !== wallHorizontal;
      if (wall === oldPlotWall) {
        updatedPlotBoundary.push(newPlotWall);
      } else if (arePerpendicular) {
        if (oldPlotWallHorizontal) {
          if (oldPlotWall.y1 === wall.y1) {
            wall = { ...wall, y1: newPlotWall.y1 };
          } else if (oldPlotWall.y1 === wall.y2) {
            wall = { ...wall, y2: newPlotWall.y1 };
          }
        } else {
          if (oldPlotWall.x1 === wall.x1) {
            wall = { ...wall, x1: newPlotWall.x1 };
          } else if (oldPlotWall.x1 === wall.x2) {
            wall = { ...wall, x2: newPlotWall.x1 };
          }
        }
        updatedPlotBoundary.push(wall);
      } else {
        updatedPlotBoundary.push(wall);
      }
    });
    dispatch(setPlot(updatedPlotBoundary));
    dispatch(setCleanUpDone(true));
  };

  const drawLine = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    drag: boolean,
    contentGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
    id: string,
    isPlotBoundary: boolean,
  ) => {
    if (contentGroupRef.current)
      contentGroup = d3.select(contentGroupRef.current);
    if (drag) removeDragLine(contentGroup);

    const line = contentGroup
      .append("line")
      .attr("id", id)
      .attr("class", isPlotBoundary ? "plot" : drag ? "drag-line" : "edge")
      .attr("x1", x1)
      .attr("y1", y1)
      .attr("x2", x2)
      .attr("y2", y2)
      .attr("stroke", isPlotBoundary ? "gray" : "#000")
      .attr("stroke-width", 2)
      .style("cursor", "pointer")
      .call(lineDrag as any)
      .on("click", (event: PointerEvent) => {
        event.stopPropagation();
        const [cx, cy] = d3.pointer(event, contentGroupRef.current);
        handleLineClick(id, cx, cy);
      });

    // line;
  };

  const removeDragLine = (
    contentGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
  ) => {
    contentGroup?.select(".drag-line").attr("class", "drag-line").remove();
  };

  const distanceToRoomBounds = (room: Room, px: number, py: number): number => {
    if (!room.walls || room.walls.length === 0) return Number.MAX_VALUE;

    const xs: number[] = [];
    const ys: number[] = [];
    room.walls.forEach((w) => {
      xs.push(w.x1, w.x2);
      ys.push(w.y1, w.y2);
    });

    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const dx = px < minX ? minX - px : px > maxX ? px - maxX : 0;
    const dy = py < minY ? minY - py : py > maxY ? py - maxY : 0;

    return Math.hypot(dx, dy);
  };

  const getNearestRoomDistance = (px: number, py: number): number => {
    if (!rooms || rooms.length === 0) return Number.MAX_VALUE;
    let minDist = Number.MAX_VALUE;
    rooms.forEach((room) => {
      const dist = distanceToRoomBounds(room, px, py);
      if (dist < minDist) minDist = dist;
    });
    return minDist;
  };

  const getDoorSwingSign = (
    x: number,
    y: number,
    isVertical: boolean,
    wall: Wall,
  ): 1 | -1 => {
    const probeOffset = 18;
    const epsilon = 0.001;
    if (isVertical) {
      const leftDist = getNearestRoomDistance(wall.x1 - probeOffset, y);
      const rightDist = getNearestRoomDistance(wall.x1 + probeOffset, y);
      if (Math.abs(leftDist - rightDist) < epsilon) return 1;
      return leftDist < rightDist ? -1 : 1;
    }
    const upDist = getNearestRoomDistance(x, wall.y1 - probeOffset);
    const downDist = getNearestRoomDistance(x, wall.y1 + probeOffset);
    if (Math.abs(upDist - downDist) < epsilon) return 1;
    return upDist < downDist ? -1 : 1;
  };

  const getAssetLengthPxForWall = (wall: Wall, sizeRatio?: number): number => {
    const wallPx = Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1);
    if (sizeRatio !== undefined) {
      const boundedRatio = Math.max(0.1, Math.min(0.5, sizeRatio));
      return wallPx * boundedRatio;
    }
    const wallUnits = calculateUnscaledWallLength(
      wall.x1,
      wall.y1,
      wall.x2,
      wall.y2,
      floorplan_width,
      floorplan_height,
    );
    let defaultPx = Math.max(8, wallPx * 0.25);
    if (wallUnits && Number.isFinite(wallUnits) && wallUnits > 0) {
      const pxPerUnit = wallPx / wallUnits;
      const targetUnits = unit === "meters" ? 0.6096 : 2; // 2 feet
      defaultPx = targetUnits * pxPerUnit;
    }
    return Math.max(wallPx * 0.1, Math.min(wallPx * 0.5, defaultPx));
  };

  const addRoomLabel = (
    x: number,
    y: number,
    text: string,
    roomId?: string | number,
  ) => {
    if (contentGroupRef.current)
      contentGroup = d3.select(contentGroupRef.current);

    let roomGroup = d3.select(contentGroupRef.current).append("g");
    roomGroup.attr("class", "room-label");

    // Calculate room area on the fly
    let areaText = "";
    if (roomId !== undefined) {
      const roomWalls = walls.filter(
        (w) => w.roomIds?.map(String).includes(String(roomId)),
      );
      if (roomWalls && roomWalls.length >= 2) {
        const TOLERANCE = 0.05;
        const pointsMatch = (
          ax: number,
          ay: number,
          bx: number,
          by: number,
        ): boolean =>
          Math.abs(ax - bx) < TOLERANCE && Math.abs(ay - by) < TOLERANCE;

        const segments = roomWalls
          .filter(
            (w) =>
              typeof w?.x1 === "number" &&
              typeof w?.y1 === "number" &&
              typeof w?.x2 === "number" &&
              typeof w?.y2 === "number",
          )
          .map((w) => ({
            x1: unscaleCoordinate(
              w.x1,
              true,
              floorplan_width,
              floorplan_height,
            ),
            y1: unscaleCoordinate(
              w.y1,
              false,
              floorplan_width,
              floorplan_height,
            ),
            x2: unscaleCoordinate(
              w.x2,
              true,
              floorplan_width,
              floorplan_height,
            ),
            y2: unscaleCoordinate(
              w.y2,
              false,
              floorplan_width,
              floorplan_height,
            ),
          }));

        let areaValue = 0;
        if (segments.length >= 2) {
          const used = new Array(segments.length).fill(false);
          const orderedPoints: [number, number][] = [];

          used[0] = true;
          orderedPoints.push([segments[0].x1, segments[0].y1]);
          orderedPoints.push([segments[0].x2, segments[0].y2]);

          let currentX = segments[0].x2;
          let currentY = segments[0].y2;

          for (let iter = 1; iter < segments.length; iter++) {
            let foundNext = false;
            for (let j = 0; j < segments.length; j++) {
              if (used[j]) continue;
              const seg = segments[j];
              if (pointsMatch(currentX, currentY, seg.x1, seg.y1)) {
                used[j] = true;
                orderedPoints.push([seg.x2, seg.y2]);
                currentX = seg.x2;
                currentY = seg.y2;
                foundNext = true;
                break;
              } else if (pointsMatch(currentX, currentY, seg.x2, seg.y2)) {
                used[j] = true;
                orderedPoints.push([seg.x1, seg.y1]);
                currentX = seg.x1;
                currentY = seg.y1;
                foundNext = true;
                break;
              }
            }
            if (!foundNext) break;
          }

          if (orderedPoints.length > 2) {
            const twiceArea = orderedPoints.reduce((acc, [x1, y1], index) => {
              const nextIndex = (index + 1) % orderedPoints.length;
              const [x2, y2] = orderedPoints[nextIndex];
              return acc + (x1 * y2 - x2 * y1);
            }, 0);
            areaValue = Math.abs(twiceArea) / 2;
          }
        }

        // Bounding box fallback if tracing failed or returned a tiny area
        if (areaValue <= 0.0001) {
          let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;
          roomWalls.forEach((w) => {
            const ux1 = unscaleCoordinate(
              w.x1,
              true,
              floorplan_width,
              floorplan_height,
            );
            const uy1 = unscaleCoordinate(
              w.y1,
              false,
              floorplan_width,
              floorplan_height,
            );
            const ux2 = unscaleCoordinate(
              w.x2,
              true,
              floorplan_width,
              floorplan_height,
            );
            const uy2 = unscaleCoordinate(
              w.y2,
              false,
              floorplan_width,
              floorplan_height,
            );
            minX = Math.min(minX, ux1, ux2);
            maxX = Math.max(maxX, ux1, ux2);
            minY = Math.min(minY, uy1, uy2);
            maxY = Math.max(maxY, uy1, uy2);
          });
          const fallbackArea = (maxX - minX) * (maxY - minY);
          areaValue = fallbackArea > 0 ? fallbackArea : 0;
        }

        let displayValue = areaValue;
        let displayUnit = "sq ft";

        if (initialUnits === "meters") {
          if (unit === "meters") {
            displayUnit = "m²";
          } else {
            displayValue = areaValue * 10.76391;
            displayUnit = "sq ft";
          }
        } else {
          if (unit === "meters") {
            displayValue = areaValue * 0.09290304;
            displayUnit = "m²";
          } else {
            displayUnit = "sq ft";
          }
        }
        areaText = `${displayValue.toFixed(2)} ${displayUnit}`;
      }
    }

    // Add text first (without positioning) to calculate its size
    let roomLabel = roomGroup
      .append("text")
      .attr("class", "text")
      .attr("fill", "#FBE5C6")
      .attr("font-weight", "bold")
      .style("font-size", "12px")
      .style("text-anchor", "middle");

    // Room name
    roomLabel
      .append("tspan")
      .text(formatLabel(text))
      .attr("x", 0)
      .attr("dy", "0em");

    // Room area if calculated
    if (areaText) {
      roomLabel
        .append("tspan")
        .text(areaText)
        .attr("x", 0)
        .attr("dy", "1.25em")
        .style("font-size", "10px")
        .style("font-weight", "normal")
        .attr("fill", "#B8D4E3");
    }

    // Get the bounding box of the text
    //@ts-ignore
    const textBBox = roomLabel.node().getBBox();

    // Define padding
    const paddingX = 10;
    const paddingY = 5;

    // Create rectangle based on text size plus padding
    let roomLabelRect = roomGroup
      .insert("rect", "text") // Insert the rect before the text
      .attr("x", textBBox.x - paddingX)
      .attr("y", textBBox.y - paddingY)
      .attr("width", textBBox.width + 2 * paddingX)
      .attr("height", textBBox.height + 2 * paddingY)
      .attr("fill", "#101010")
      .attr("rx", 5) // Horizontal radius of the corner curvature
      .attr("ry", 5)
      .attr("opacity", 0.7);

    // Center the entire group at the provided (x, y) coordinates
    roomGroup.attr(
      "transform",
      `translate(${x}, ${y - (textBBox.y + textBBox.height / 2)})`,
    );
    roomGroup.raise();
  };

  const attachResizeHandles = (
    group: d3.Selection<SVGGElement, unknown, null, undefined>,
    assetId: string,
    x: number,
    y: number,
    isVertical: boolean,
    assetType: "door" | "window",
    wall?: Wall,
  ) => {
    if (!wall) return;
    const wallPx = Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1);
    
    // Resolve current sizeRatio and length
    const getCurAsset = () => {
      return assetType === "door"
        ? (doorAssetsRef.current || []).find((a: any) => a.id === assetId)
        : (windowAssetsRef.current || []).find((a: any) => a.id === assetId);
    };
    
    const asset = getCurAsset();
    const lengthPx = getAssetLengthPxForWall(wall, asset?.sizeRatio);
    const halfLen = lengthPx / 2;

    const handle1 = group
      .append("circle")
      .attr("class", "resize-handle handle-1")
      .attr("r", 5)
      .style("cursor", isVertical ? "ns-resize" : "ew-resize");

    const handle2 = group
      .append("circle")
      .attr("class", "resize-handle handle-2")
      .attr("r", 5)
      .style("cursor", isVertical ? "ns-resize" : "ew-resize");

    if (isVertical) {
      handle1.attr("cx", x).attr("cy", y - halfLen);
      handle2.attr("cx", x).attr("cy", y + halfLen);
    } else {
      handle1.attr("cx", x - halfLen).attr("cy", y);
      handle2.attr("cx", x + halfLen).attr("cy", y);
    }

    let doorAssetsBeforeResize: any[] = [];
    let windowAssetsBeforeResize: any[] = [];
    
    const resizeDrag = d3
      .drag<SVGCircleElement, unknown>()
      .on("start", function (event) {
        event.sourceEvent.stopPropagation();
        doorAssetsBeforeResize = doorAssetsRef.current || [];
        windowAssetsBeforeResize = windowAssetsRef.current || [];
      })
      .on("drag", function (event) {
        event.sourceEvent.stopPropagation();
        const pointer = d3.pointer(event.sourceEvent || event, svgRef.current);
        const pointerX = pointer[0];
        const pointerY = pointer[1];
        
        let newHalfLen = 20;
        if (isVertical) {
          newHalfLen = Math.abs(pointerY - y);
        } else {
          newHalfLen = Math.abs(pointerX - x);
        }

        const newLength = 2 * newHalfLen;
        const minPx = wallPx * 0.1;
        const maxPx = wallPx * 0.5;
        const clampedLength = Math.max(minPx, Math.min(maxPx, newLength));
        const clampedHalfLen = clampedLength / 2;

        if (assetType === "door") {
          const curA = getCurAsset() as any;
          const swingSign = curA?.swingSign || 1;
          if (isVertical) {
            group.select(".door-bg")
              .attr("y1", y - clampedHalfLen)
              .attr("y2", y + clampedHalfLen);
            
            const hingeY = y - clampedHalfLen;
            const closedY = y + clampedHalfLen;
            const openY = hingeY;
            const openX = x + swingSign * clampedLength;

            group.select(".door-leaf")
              .attr("y1", hingeY)
              .attr("x2", openX)
              .attr("y2", openY);

            const closedAngle = Math.atan2(closedY - hingeY, 0);
            const openAngle = Math.atan2(openY - hingeY, openX - x);
            const cross = (x - x) * (openY - hingeY) - (closedY - hingeY) * (openX - x);
            const arcPath = d3.path();
            arcPath.arc(x, hingeY, clampedLength, closedAngle, openAngle, cross < 0);
            group.select(".door-arc").attr("d", arcPath.toString());
          } else {
            group.select(".door-bg")
              .attr("x1", x - clampedHalfLen)
              .attr("x2", x + clampedHalfLen);

            const hingeX = x - clampedHalfLen;
            const closedX = x + clampedHalfLen;
            const openX = hingeX;
            const openY = y + swingSign * clampedLength;

            group.select(".door-leaf")
              .attr("x1", hingeX)
              .attr("x2", openX)
              .attr("y2", openY);

            const closedAngle = Math.atan2(0, closedX - hingeX);
            const openAngle = Math.atan2(openY - y, openX - hingeX);
            const cross = (closedX - hingeX) * (openY - y) - (y - y) * (openX - hingeX);
            const arcPath = d3.path();
            arcPath.arc(hingeX, y, clampedLength, closedAngle, openAngle, cross < 0);
            group.select(".door-arc").attr("d", arcPath.toString());
          }
        } else {
          if (isVertical) {
            group.select(".window-bg").attr("y1", y - clampedHalfLen).attr("y2", y + clampedHalfLen);
            group.select(".window-frame-1").attr("y1", y - clampedHalfLen).attr("y2", y + clampedHalfLen);
            group.select(".window-frame-2").attr("y1", y - clampedHalfLen).attr("y2", y + clampedHalfLen);
            group.select(".window-center").attr("y1", y - clampedHalfLen).attr("y2", y + clampedHalfLen);
            group.select(".window-cap-1").attr("y1", y - clampedHalfLen).attr("y2", y - clampedHalfLen);
            group.select(".window-cap-2").attr("y1", y + clampedHalfLen).attr("y2", y + clampedHalfLen);
          } else {
            group.select(".window-bg").attr("x1", x - clampedHalfLen).attr("x2", x + clampedHalfLen);
            group.select(".window-frame-1").attr("x1", x - clampedHalfLen).attr("x2", x + clampedHalfLen);
            group.select(".window-frame-2").attr("x1", x - clampedHalfLen).attr("x2", x + clampedHalfLen);
            group.select(".window-center").attr("x1", x - clampedHalfLen).attr("x2", x + clampedHalfLen);
            group.select(".window-cap-1").attr("x1", x - clampedHalfLen).attr("x2", x - clampedHalfLen);
            group.select(".window-cap-2").attr("x1", x + clampedHalfLen).attr("x2", x + clampedHalfLen);
          }
        }

        if (isVertical) {
          group.select(".handle-1").attr("cy", y - clampedHalfLen);
          group.select(".handle-2").attr("cy", y + clampedHalfLen);
        } else {
          group.select(".handle-1").attr("cx", x - clampedHalfLen);
          group.select(".handle-2").attr("cx", x + clampedHalfLen);
        }
      })
      .on("end", function (event) {
        event.sourceEvent.stopPropagation();
        const pointer = d3.pointer(event.sourceEvent || event, svgRef.current);
        const pointerX = pointer[0];
        const pointerY = pointer[1];
        
        let finalHalfLen = 20;
        if (isVertical) {
          finalHalfLen = Math.abs(pointerY - y);
        } else {
          finalHalfLen = Math.abs(pointerX - x);
        }
        const finalLength = 2 * finalHalfLen;
        const minPx = wallPx * 0.1;
        const maxPx = wallPx * 0.5;
        const clampedLength = Math.max(minPx, Math.min(maxPx, finalLength));
        const finalRatio = clampedLength / wallPx;

        if (assetType === "door") {
          const updated = doorAssetsBeforeResize.map((a: any) =>
            a.id === assetId ? { ...a, sizeRatio: finalRatio } : a
          );
          dispatch(
            pushUndoStacks({
              rooms,
              walls,
              plot: plot_boundary,
              doorAssets: doorAssetsBeforeResize,
              windowAssets: windowAssetsBeforeResize,
            }),
          );
          dispatch(clearRedoStacks());
          dispatch(setDoorAssets(updated));
        } else {
          const updated = windowAssetsBeforeResize.map((a: any) =>
            a.id === assetId ? { ...a, sizeRatio: finalRatio } : a
          );
          dispatch(
            pushUndoStacks({
              rooms,
              walls,
              plot: plot_boundary,
              doorAssets: doorAssetsBeforeResize,
              windowAssets: windowAssetsBeforeResize,
            }),
          );
          dispatch(clearRedoStacks());
          dispatch(setWindowAssets(updated));
        }
      });

    handle1.call(resizeDrag);
    handle2.call(resizeDrag);
  };

  const addAsset = (
    width: number,
    height: number,
    contentGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
    assetData: any, // assetData (which includes id, x, y, isVertical)
    wall: Wall, // Pass the entire wall object
    assetType: "door" | "window", // New parameter to specify asset type
  ) => {
    let minX = 1e9;
    let maxX = -1e9;
    let minY = 1e9;
    let maxY = -1e9;

    // Use the global 'walls' state for calculating min/max coordinates
    walls?.forEach((w) => {
      minX = Math.min(minX, w.x1, w.x2);
      minY = Math.min(minY, w.y1, w.y2);
      maxX = Math.max(maxX, w.x1, w.x2);
      maxY = Math.max(maxY, w.y1, w.y2);
    });
    const w = Math.abs(maxX - minX);
    const h = Math.abs(maxY - minY);

    if (contentGroupRef.current)
      contentGroup = d3.select(contentGroupRef.current);

    // Directly use wall's properties instead of selecting by ID
    const x1 = wall.x1;
    const y1 = wall.y1;
    const x2 = wall.x2;
    const y2 = wall.y2;
    const lineId = wall.id; // Use wall's ID as lineId

    let wallLength = calculateUnscaledWallLength(
      x1,
      y1,
      x2,
      y2,
      floorplan_width,
      floorplan_height,
    );
    if (wallLength < 2) {
      return;
    }

    let rotationAngle = 0;

    let asset: any;
    const ASSET_RAW_LENGTH = getAssetLengthPxForWall(wall, assetData.sizeRatio);
    const WINDOW_SIDE_LINE_OFFSET = 2;

    if (assetType === "door") {
      const doorGroup = contentGroup.append("g").attr("class", "door-group");
      asset = doorGroup;
      const isVertical = wall.x1 === wall.x2;
      const dLength = ASSET_RAW_LENGTH;
      const swingSign: 1 | -1 =
        assetData.swingSign ||
        getDoorSwingSign(assetData.x, assetData.y, isVertical, wall);

      if (isVertical) {
        const x = assetData.x;
        const y = assetData.y;
        const hingeX = x;
        const hingeY = y - dLength / 2;
        const closedX = x;
        const closedY = y + dLength / 2;
        const openX = x + swingSign * dLength;
        const openY = hingeY;

        doorGroup
          .append("line")
          .attr("class", "door-bg")
          .attr("x1", x)
          .attr("y1", y - dLength / 2)
          .attr("x2", x)
          .attr("y2", y + dLength / 2)
          .attr("stroke", "#FBE5C6")
          .attr("stroke-width", 8);

        doorGroup
          .append("line")
          .attr("class", "door-leaf")
          .attr("x1", hingeX)
          .attr("y1", hingeY)
          .attr("x2", openX)
          .attr("y2", openY)
          .attr("stroke", "#000")
          .attr("stroke-width", 2)
          .attr("stroke-linecap", "round");

        // Use d3.path().arc() to draw a proper quarter-circle centered at hinge
        const closedAngle = Math.atan2(closedY - hingeY, closedX - hingeX);
        const openAngle = Math.atan2(openY - hingeY, openX - hingeX);
        const cross =
          (closedX - hingeX) * (openY - hingeY) -
          (closedY - hingeY) * (openX - hingeX);
        const arcPath = d3.path();
        arcPath.arc(hingeX, hingeY, dLength, closedAngle, openAngle, cross < 0);

        doorGroup
          .append("path")
          .attr("class", "door-arc")
          .attr("d", arcPath.toString())
          .attr("fill", "none")
          .attr("stroke", "#000")
          .attr("stroke-width", 2);
      } else {
        const x = assetData.x;
        const y = assetData.y;
        const hingeX = x - dLength / 2;
        const hingeY = y;
        const closedX = x + dLength / 2;
        const closedY = y;
        const openX = hingeX;
        const openY = y + swingSign * dLength;

        doorGroup
          .append("line")
          .attr("class", "door-bg")
          .attr("x1", x - dLength / 2)
          .attr("y1", y)
          .attr("x2", x + dLength / 2)
          .attr("y2", y)
          .attr("stroke", "#FBE5C6")
          .attr("stroke-width", 8);

        doorGroup
          .append("line")
          .attr("class", "door-leaf")
          .attr("x1", hingeX)
          .attr("y1", hingeY)
          .attr("x2", openX)
          .attr("y2", openY)
          .attr("stroke", "#000")
          .attr("stroke-width", 2)
          .attr("stroke-linecap", "round");

        // Use d3.path().arc() to draw a proper quarter-circle centered at hinge
        const closedAngle = Math.atan2(closedY - hingeY, closedX - hingeX);
        const openAngle = Math.atan2(openY - hingeY, openX - hingeX);
        const cross =
          (closedX - hingeX) * (openY - hingeY) -
          (closedY - hingeY) * (openX - hingeX);
        const arcPath = d3.path();
        arcPath.arc(hingeX, hingeY, dLength, closedAngle, openAngle, cross < 0);

        doorGroup
          .append("path")
          .attr("class", "door-arc")
          .attr("d", arcPath.toString())
          .attr("fill", "none")
          .attr("stroke", "#000")
          .attr("stroke-width", 2);
      }
    } else if (assetType === "window") {
      // Classic Architectural Window: A white cutout and clean parallel lines
      const windowGroup = contentGroup
        .append("g")
        .attr("class", "window-group");
      asset = windowGroup;
      const isVertical = wall.x1 === wall.x2;

      const wLength = ASSET_RAW_LENGTH;
      // We assume wall thickness is roughly 8
      const wallThickness = 8;

      if (isVertical) {
        const topY = assetData.y - wLength / 2;
        const bottomY = assetData.y + wLength / 2;

        // 1. Cutout the wall
        windowGroup
          .append("line")
          .attr("class", "window-bg")
          .attr("x1", assetData.x)
          .attr("y1", topY)
          .attr("x2", assetData.x)
          .attr("y2", bottomY)
          .attr("stroke", "#FBE5C6")
          .attr("stroke-width", wallThickness + 2); // fully cut wall

        // 2. Window frame lines (outer boundaries)
        windowGroup
          .append("line")
          .attr("class", "window-frame-1")
          .attr("x1", assetData.x - wallThickness / 2)
          .attr("y1", topY)
          .attr("x2", assetData.x - wallThickness / 2)
          .attr("y2", bottomY)
          .attr("stroke", "#FF9A56")
          .attr("stroke-width", 1.5);

        windowGroup
          .append("line")
          .attr("class", "window-frame-2")
          .attr("x1", assetData.x + wallThickness / 2)
          .attr("y1", topY)
          .attr("x2", assetData.x + wallThickness / 2)
          .attr("y2", bottomY)
          .attr("stroke", "#FF9A56")
          .attr("stroke-width", 1.5);

        // 3. Center glass pane line
        windowGroup
          .append("line")
          .attr("class", "window-center")
          .attr("x1", assetData.x)
          .attr("y1", topY)
          .attr("x2", assetData.x)
          .attr("y2", bottomY)
          .attr("stroke", "#FF9A56")
          .attr("stroke-width", 1);

        // 4. End caps
        windowGroup
          .append("line")
          .attr("class", "window-cap-1")
          .attr("x1", assetData.x - wallThickness / 2)
          .attr("y1", topY)
          .attr("x2", assetData.x + wallThickness / 2)
          .attr("y2", topY)
          .attr("stroke", "#FF9A56")
          .attr("stroke-width", 1.5);
        windowGroup
          .append("line")
          .attr("class", "window-cap-2")
          .attr("x1", assetData.x - wallThickness / 2)
          .attr("y1", bottomY)
          .attr("x2", assetData.x + wallThickness / 2)
          .attr("y2", bottomY)
          .attr("stroke", "#FF9A56")
          .attr("stroke-width", 1.5);
      } else {
        // Horizontal wall
        const leftX = assetData.x - wLength / 2;
        const rightX = assetData.x + wLength / 2;

        // 1. Cutout the wall
        windowGroup
          .append("line")
          .attr("class", "window-bg")
          .attr("x1", leftX)
          .attr("y1", assetData.y)
          .attr("x2", rightX)
          .attr("y2", assetData.y)
          .attr("stroke", "#FBE5C6")
          .attr("stroke-width", wallThickness + 2);

        // 2. Window frame lines (outer boundaries)
        windowGroup
          .append("line")
          .attr("class", "window-frame-1")
          .attr("x1", leftX)
          .attr("y1", assetData.y - wallThickness / 2)
          .attr("x2", rightX)
          .attr("y2", assetData.y - wallThickness / 2)
          .attr("stroke", "#FF9A56")
          .attr("stroke-width", 1.5);

        windowGroup
          .append("line")
          .attr("class", "window-frame-2")
          .attr("x1", leftX)
          .attr("y1", assetData.y + wallThickness / 2)
          .attr("x2", rightX)
          .attr("y2", assetData.y + wallThickness / 2)
          .attr("stroke", "#FF9A56")
          .attr("stroke-width", 1.5);

        // 3. Center glass pane line
        windowGroup
          .append("line")
          .attr("class", "window-center")
          .attr("x1", leftX)
          .attr("y1", assetData.y)
          .attr("x2", rightX)
          .attr("y2", assetData.y)
          .attr("stroke", "#FF9A56")
          .attr("stroke-width", 1);

        // 4. End caps
        windowGroup
          .append("line")
          .attr("class", "window-cap-1")
          .attr("x1", leftX)
          .attr("y1", assetData.y - wallThickness / 2)
          .attr("x2", leftX)
          .attr("y2", assetData.y + wallThickness / 2)
          .attr("stroke", "#FF9A56")
          .attr("stroke-width", 1.5);
        windowGroup
          .append("line")
          .attr("class", "window-cap-2")
          .attr("x1", rightX)
          .attr("y1", assetData.y - wallThickness / 2)
          .attr("x2", rightX)
          .attr("y2", assetData.y + wallThickness / 2)
          .attr("stroke", "#FF9A56")
          .attr("stroke-width", 1.5);
      }
    }

    dispatch(setAssetType(null));
    // Enable drag on the group element
    if (asset) {
      asset.style("cursor", "pointer")
        .on("click", (event: PointerEvent) => {
          const currentActiveTool = activeToolRef.current;
          if (currentActiveTool === "delete") {
            event.stopPropagation();
            if (assetType === "door") {
              const updated = (doorAssetsRef.current || []).filter(
                (d: any) => d.id !== assetData.id
              );
              dispatch(setDoorAssets(updated));
            } else {
              const updated = (windowAssetsRef.current || []).filter(
                (w: any) => w.id !== assetData.id
              );
              dispatch(setWindowAssets(updated));
            }
            dispatch(setCleanUpDone(false));
            setTimeout(() => dispatch(setCleanUpDone(true)), 10);
          }
        });
      setupAssetDrag(asset, lineId, assetData, assetType, wall);
      attachResizeHandles(asset, assetData.id, assetData.x, assetData.y, wall.x1 === wall.x2, assetType, wall);

      // Add a dedicated floating delete button (red circle with white x) next to the door/window element
      const isVertical = wall.x1 === wall.x2;
      const deleteBtn = asset.append("g")
        .attr("class", "asset-delete-btn")
        .style("cursor", "pointer")
        .on("click", (event: PointerEvent) => {
          event.stopPropagation();
          if (assetType === "door") {
            const updated = (doorAssetsRef.current || []).filter(
              (d: any) => d.id !== assetData.id
            );
            dispatch(setDoorAssets(updated));
          } else {
            const updated = (windowAssetsRef.current || []).filter(
              (w: any) => w.id !== assetData.id
            );
            dispatch(setWindowAssets(updated));
          }
          dispatch(setCleanUpDone(false));
          setTimeout(() => dispatch(setCleanUpDone(true)), 10);
        });

      const btnOffset = 16;
      const btnX = isVertical ? assetData.x + btnOffset : assetData.x;
      const btnY = isVertical ? assetData.y : assetData.y - btnOffset;

      deleteBtn.append("circle")
        .attr("cx", btnX)
        .attr("cy", btnY)
        .attr("r", 7)
        .attr("fill", "#ff4d4f")
        .attr("stroke", "#fff")
        .attr("stroke-width", 1);

      deleteBtn.append("text")
        .attr("x", btnX)
        .attr("y", btnY + 3)
        .attr("text-anchor", "middle")
        .attr("fill", "#fff")
        .style("font-size", "10px")
        .style("font-weight", "bold")
        .style("user-select", "none")
        .text("×");
    }
  };

  // Drag handler for door/window group elements.
  // Uses translate transform so the entire group (all child shapes) moves together.
  const setupAssetDrag = (
    assetGroup: any,
    lineId: string,
    assetData: any,
    assetType: "door" | "window",
    assetWall: Wall,
  ) => {
    const line = d3.select(`[id="${lineId}"]`);
    const x1 = parseFloat(line.attr("x1"));
    const y1 = parseFloat(line.attr("y1"));
    const x2 = parseFloat(line.attr("x2"));
    const y2 = parseFloat(line.attr("y2"));

    const isHorizontalWall = y1 === y2;
    let offsetX = 0;
    let offsetY = 0;

    const drag = d3
      .drag()
      .on("start", function (event) {
        d3.select(this).raise().classed("active", true);
        offsetX = 0;
        offsetY = 0;
      })
      .on("drag", function (event) {
        if (isHorizontalWall) {
          // Constrain movement along the wall (x-axis)
          offsetX += event.dx;
          const currentX = assetData.x + offsetX;
          // Clamp within wall bounds
          const halfLen = getAssetLengthPxForWall(assetWall, assetData.sizeRatio) / 2;
          const minBound = Math.min(x1, x2) + halfLen;
          const maxBound = Math.max(x1, x2) - halfLen;
          if (currentX < minBound) offsetX = minBound - assetData.x;
          if (currentX > maxBound) offsetX = maxBound - assetData.x;
          d3.select(this).attr("transform", `translate(${offsetX}, 0)`);
        } else {
          // Constrain movement along the wall (y-axis)
          offsetY += event.dy;
          const currentY = assetData.y + offsetY;
          const halfLen = getAssetLengthPxForWall(assetWall, assetData.sizeRatio) / 2;
          const minBound = Math.min(y1, y2) + halfLen;
          const maxBound = Math.max(y1, y2) - halfLen;
          if (currentY < minBound) offsetY = minBound - assetData.y;
          if (currentY > maxBound) offsetY = maxBound - assetData.y;
          d3.select(this).attr("transform", `translate(0, ${offsetY})`);
        }
      })
      .on("end", function (event) {
        d3.select(this).classed("active", false);

        // Compute final absolute position after drag
        const finalX = isHorizontalWall ? assetData.x + offsetX : assetData.x;
        const finalY = isHorizontalWall ? assetData.y : assetData.y + offsetY;

        // Skip dispatch if position didn't change
        if (finalX === assetData.x && finalY === assetData.y) return;

        // Persist the new position to Redux (using refs to avoid stale closures)
        if (assetType === "door") {
          const isVertical = !isHorizontalWall;
          const swingSign = getDoorSwingSign(
            finalX,
            finalY,
            isVertical,
            assetWall,
          );
          const currentAssets = doorAssetsRef.current || [];
          const updated = currentAssets.map((a: any) =>
            a.id === assetData.id
              ? { ...a, x: finalX, y: finalY, swingSign }
              : a,
          );
          dispatch(setDoorAssets(updated));
        } else {
          const currentAssets = windowAssetsRef.current || [];
          const updated = currentAssets.map((a: any) =>
            a.id === assetData.id ? { ...a, x: finalX, y: finalY } : a,
          );
          dispatch(setWindowAssets(updated));
        }
      });

    assetGroup.style("cursor", "grab").call(drag);
  };
  /**
   * Given a clicked wall and optional click coordinates, find a better
   * overlapping wall if one exists. Priority:
   * 1. Prefer common walls (shared between more rooms, i.e. more roomIds).
   * 2. Among walls with equal roomIds count, prefer the shorter wall
   *    (more specific to the intersection the user clicked).
   * Uses tolerance-based matching to handle floating-point coordinate differences.
   */
  function findBestOverlappingWall(
    clickedWall: Wall,
    cx?: number,
    cy?: number,
  ): Wall {
    const currentWalls = wallsRef.current;
    if (cx == null || cy == null || !currentWalls) return clickedWall;

    const AXIS_TOLERANCE = 5; // pixels tolerance for same-axis detection
    const SPAN_TOLERANCE = 5; // pixels tolerance for click-in-span check

    const clickedIsHorizontal = clickedWall.y1 === clickedWall.y2;
    const clickedIsVertical = clickedWall.x1 === clickedWall.x2;

    // If the wall is neither purely horizontal nor vertical, skip
    if (!clickedIsHorizontal && !clickedIsVertical) return clickedWall;

    let bestWall = clickedWall;
    let bestWallLength = Math.hypot(
      clickedWall.x2 - clickedWall.x1,
      clickedWall.y2 - clickedWall.y1,
    );

    currentWalls.forEach((w) => {
      if (w.id === clickedWall.id) return;

      if (clickedIsHorizontal) {
        // Candidate must also be horizontal and on a similar y-axis line
        if (w.y1 !== w.y2) return;
        if (Math.abs(w.y1 - clickedWall.y1) > AXIS_TOLERANCE) return;
        // Check the click point is within this wall's x-span
        const minX = Math.min(w.x1, w.x2);
        const maxX = Math.max(w.x1, w.x2);
        if (cx < minX - SPAN_TOLERANCE || cx > maxX + SPAN_TOLERANCE) return;
      } else {
        // Candidate must also be vertical and on a similar x-axis line
        if (w.x1 !== w.x2) return;
        if (Math.abs(w.x1 - clickedWall.x1) > AXIS_TOLERANCE) return;
        // Check the click point is within this wall's y-span
        const minY = Math.min(w.y1, w.y2);
        const maxY = Math.max(w.y1, w.y2);
        if (cy < minY - SPAN_TOLERANCE || cy > maxY + SPAN_TOLERANCE) return;
      }

      const wRoomCount = w.roomIds?.length || 0;
      const bestRoomCount = bestWall.roomIds?.length || 0;
      const wLength = Math.hypot(w.x2 - w.x1, w.y2 - w.y1);

      // Prefer walls with more roomIds (common walls), then shorter walls
      if (wRoomCount > bestRoomCount) {
        bestWall = w;
        bestWallLength = wLength;
      } else if (wRoomCount === bestRoomCount && wLength < bestWallLength) {
        bestWall = w;
        bestWallLength = wLength;
      }
    });

    return bestWall;
  }

  function handleLineClick(lineId: string, cx?: number, cy?: number) {
    // Read from refs to avoid stale closure values
    const currentActiveTool = activeToolRef.current;
    const currentDraw = drawRef.current;
    const currentWalls = wallsRef.current;
    const currentSelectedWallOption = selectedWallOptionRef.current;

    if (!currentDraw && (currentActiveTool === "door" || currentActiveTool === "window")) {
      const wall = currentWalls?.find((w) => w.id === lineId);
      if (wall) {
        const targetWall = findBestOverlappingWall(wall, cx, cy);
        const assetX = targetWall.x1 === targetWall.x2 ? targetWall.x1 : cx!;
        const assetY = targetWall.y1 === targetWall.y2 ? targetWall.y1 : cy!;

        if (currentActiveTool === "door") {
          const newDoor = {
            id: `door-${uuidv4()}`,
            x: assetX,
            y: assetY,
            isVertical: targetWall.x1 === targetWall.x2,
            sizeRatio: 0.25,
          };
          dispatch(setDoorAssets([...(doorAssetsRef.current || []), newDoor]));
        } else {
          const newWindow = {
            id: `window-${uuidv4()}`,
            x: assetX,
            y: assetY,
            isVertical: targetWall.x1 === targetWall.x2,
            sizeRatio: 0.25,
          };
          dispatch(setWindowAssets([...(windowAssetsRef.current || []), newWindow]));
        }

        dispatch(setCleanUpDone(false));
        setTimeout(() => dispatch(setCleanUpDone(true)), 10);
        return;
      }
    } else if (!currentDraw && currentActiveTool === "delete") {
      let wall = currentWalls?.find((wall) => wall.id === lineId);

      if (wall) {
        wall = findBestOverlappingWall(wall, cx, cy);
        deleteWall(wall);
        return;
      }
    } else if (
      !currentDraw &&
      (currentActiveTool === "select" ||
        currentActiveTool === "arrow" ||
        currentActiveTool === null)
    ) {
      let wall = currentWalls?.find((wall) => wall.id === lineId);

      if (wall) {
        // If there's a common wall overlapping at the click point, prefer it
        wall = findBestOverlappingWall(wall, cx, cy);
        const targetId = wall.id;

        d3.selectAll(".edge").classed("highlighted", false);

        // Highlight the selected wall
        d3.select(`[id="${targetId}"]`).classed("highlighted", true);

        setSelectedLineId(targetId);
        dispatch(setSelectedWall(wall));

        // If select or arrow tool is active, prepare the wall for dragging
        if (
          currentActiveTool === "select" ||
          currentActiveTool === "arrow" ||
          currentActiveTool === null
        ) {
          selectedRef.current = true;
          selectedWallRef.current = wall;
        }
      } else {
        // Check for plot boundaries
        const plot_wall = plot_boundary?.find((wall) => wall.id === lineId);

        if (plot_wall) {
          // Clear any existing highlights
          d3.selectAll(".edge").classed("highlighted", false);
          d3.selectAll(".plot").classed("highlighted", false);

          // Highlight the selected plot boundary
          d3.select(`[id="${lineId}"]`).classed("highlighted", true);

          setSelectedLineId(lineId);
          dispatch(setSelectedWall(plot_wall));

          // If select or arrow tool is active, prepare the plot boundary for dragging
          if (
            currentActiveTool === "select" ||
            currentActiveTool === "arrow" ||
            currentActiveTool === null
          ) {
            selectedRef.current = true;
            selectedWallRef.current = plot_wall;
          }
        }
      }
    }
  }

  useEffect(() => {
    // console.log("walls", walls);
  }, [walls]);
  useEffect(() => {
    if (selectedLineId && newAsset?.assetType) {
      // console.log("calling add asset from here!!", newAsset);
      // addAsset(2, 2, contentGroup, selectedLineId);
    } else console.log("line not selected");
  }, [newAsset]);

  useEffect(() => {
    if (selectedWall) {
      if (selectedWallOption === "unselect") {
        d3.select(`[id="${selectedWall.id}"]`).classed("highlighted", false);
        dispatch(setSelectedWall(undefined));
        selectedRef.current = false;
        selectedWallRef.current = undefined;
        dispatch(setSelectedWallOption(null));
      } else if (selectedWallOption === "delete") {
        console.log("Delete option selected for wall:", selectedWall.id);
        d3.select(`[id="${selectedWall.id}"]`).classed("highlighted", false);
        deleteWall(selectedWall);
        dispatch(setSelectedWall(undefined));
        selectedRef.current = false;
        selectedWallRef.current = undefined;
        dispatch(setSelectedWallOption(null));
      } else if (
        activeTool === "select" ||
        activeTool === "arrow" ||
        activeTool === null
      ) {
        d3.selectAll(".edge").classed("highlighted", false);
        d3.selectAll(".plot").classed("highlighted", false);
        d3.select(`[id="${selectedWall.id}"]`).classed("highlighted", true);
        selectedRef.current = true;
        selectedWallRef.current = selectedWall;
        // Don't clear the selected wall immediately so it can be dragged
      } else {
        d3.select(`[id="${selectedWall.id}"]`).classed("highlighted", false);
        dispatch(setSelectedWall(undefined));
        selectedRef.current = false;
        selectedWallRef.current = undefined;
      }
    }
  }, [selectedWallOption, selectedWall, activeTool]);

  // Add a separate effect to handle tool change deselection
  useEffect(() => {
    // When the active tool changes to anything other than select or arrow,
    // deselect any selected wall
    if (
      activeTool !== "select" &&
      activeTool !== "arrow" &&
      activeTool !== null &&
      selectedWall
    ) {
      selectedRef.current = false;
      selectedWallRef.current = undefined;
    }
  }, [activeTool]);

  return { handleLineClick };
};
